// =====================================================================
//  How a bot plays. The game server asks `botNext` what the bot does next, does it (through the same rules
//  engine as a player's moves), waits the step's `delay`, and asks again.
//
//  The bot plays the table the way a player does: it pays for and places cards, attacks, uses its Tap
//  abilities and its Emperor's abilities, and answers the choices its cards ask for. The table does the rest
//  by itself, as it does for a player: the damage of an attack (Shield, Buffer, Thorns, Entrenched…), what a
//  card's effects do, what is destroyed. (On a table set to leave the damage to the players, CONFIG.combat
//  off, the bot puts its own damage counters on and announces what its cards do.) Before it moves on from a
//  phase in which it did something, it asks ("Martin wants to move to the Attack Phase") and waits for a Go
//  ahead.
//
//  How well it attacks depends on its level. Martin (easy) attacks with what he has at whatever he can reach,
//  forgets a unit now and then and never uses his Emperor's abilities. Practice Buddy (skilled) tries each
//  attack on a copy of the game and takes the best for each unit in turn, with a little slack. Creator
//  (expert) weighs every attack and Tap he has against every target each time, makes the best one, and holds
//  back a unit whose attack would do more harm than good (it earns Fractals untapped instead).
//
//  It never looks at its opponent's hand or deck: only at the table, its own hand and its own Gerzite.
//  How well it chooses depends on its level (easy, skilled, expert): see the choices below.
// =====================================================================
import { CONFIG } from '../../config.ts';
import { formOf, isEmperor, isSecretRare, type CardDb, type CardDef } from '../cards.ts';
import type { TokenDb } from '../tokens.ts';
import { countsAgainstHealth, handOf, sideOf, slotKind, spanStarts, laneOrder, tokensOnCard, type Action, type FieldCard, type Game, type Seat, type Token } from '../engine.ts';
import { cardPower, numbersIn, type BotLevel } from './botDeck.ts';
import { seededRng } from '../match.ts';

/**
 * The bots, weakest first. Each has a way of building its deck (BotProfile.deck, see buildBotDeck) and a way of
 * playing: `play` says how often (per phase) it plays like Martin (easy), Practice Buddy (skilled) or Creator
 * (expert), so the ones between play mostly one way with now and then the next one up. The Forsaken also tries out
 * its choices on a copy of the game before it makes them (look-ahead). `counts`: its decks count in the online card
 * stats. `pace`: how quickly it moves (1 = Practice Buddy's speed; less is quicker).
 */
export interface BotProfile {
  id: string;
  name: string;
  rank: number; // difficulty out of 9
  blurb: string;
  play: [number, number, number]; // weights for playing easy / skilled / expert, chosen afresh each phase
  deck: 'random' | 'built' | 'creator' | 'mixed';
  build?: { candidates: number; swaps: number; stats: 'skilled' | 'expert' }; // a deck it builds itself ('built', and 'mixed' half the time)
  lookahead?: number; // The Forsaken: how many ways of playing each phase it tries out on a copy of the game first
  counts: boolean;
  pace: number;
  tier: BotLevel; // how it chooses what its cards' effects aim at, on the table itself (Martin, Practice Buddy, Creator)
}
export const BOTS: BotProfile[] = [
  { id: 'easy', name: 'Martin', rank: 1, blurb: 'Throws a deck together and plays it loosely. Good for learning the table.', play: [1, 0, 0], deck: 'random', counts: false, pace: 1.25, tier: 'easy' },
  { id: 'keyvon', name: 'KeyVon', rank: 2, blurb: 'Plays a Random Deck and mostly plays it loosely, but now and then makes a sharp move.', play: [0.4, 0.6, 0], deck: 'random', counts: false, pace: 1.2, tier: 'easy' },
  { id: 'jaqueef', name: 'Jaqueef', rank: 3, blurb: 'Tries to build his own deck, with mixed results, and plays a little better than KeyVon.', play: [0.5, 0.5, 0], deck: 'built', build: { candidates: 2, swaps: 40, stats: 'skilled' }, counts: true, pace: 1.15, tier: 'easy' },
  { id: 'trainingpal', name: 'Training Pal', rank: 4, blurb: 'Builds a decent deck with a theme, and plays steadily with the odd slip.', play: [0.3, 0.7, 0], deck: 'built', build: { candidates: 3, swaps: 110, stats: 'skilled' }, counts: true, pace: 1.1, tier: 'skilled' },
  { id: 'learningamigo', name: 'Learning Amigo', rank: 5, blurb: 'Builds a good deck and makes smart moves most of the time. Just short of Practice Buddy.', play: [0.22, 0.78, 0], deck: 'built', build: { candidates: 4, swaps: 180, stats: 'skilled' }, counts: true, pace: 1.05, tier: 'skilled' },
  { id: 'skilled', name: 'Practice Buddy', rank: 6, blurb: 'Builds a solid deck and plays it sensibly: good practice for real games.', play: [0, 1, 0], deck: 'built', build: { candidates: 6, swaps: 260, stats: 'skilled' }, counts: true, pace: 1, tier: 'skilled' },
  { id: 'grandmaster', name: 'The Grandmaster', rank: 7, blurb: 'Tuned decks and sharp, patient play. A good foe for seasoned players.', play: [0, 0.4, 0.6], deck: 'mixed', build: { candidates: 10, swaps: 600, stats: 'expert' }, counts: true, pace: 0.9, tier: 'expert' },
  { id: 'expert', name: 'The Creator', rank: 8, blurb: 'Builds a tuned deck and plays sharp, punishing every opening you leave.', play: [0, 0, 1], deck: 'creator', counts: true, pace: 0.85, tier: 'expert' },
  { id: 'forsaken', name: 'The Forsaken', rank: 9, blurb: 'The strongest bot there is. Plays The Creator’s decks with almost no mistakes, sees your plays coming and punishes every slip. Expect to lose.', play: [0, 0, 1], deck: 'creator', lookahead: 6, counts: true, pace: 0.8, tier: 'expert' },
];
export const botByLevel = (id: string) => BOTS.find((b) => b.id === id);
/** A bot's profile from its id (an unknown one plays as Martin). */
export const botProfile = (id: string): BotProfile => botByLevel(id) ?? BOTS[0];

/** How long the bot takes over each kind of step (milliseconds): slow enough to follow, quick enough not to drag. */
export const BOT_PACE = {
  step: { easy: 1100, skilled: 950, expert: 850 } as Record<BotLevel, number>,
  turnStart: 1500, // a moment to look over the table as its turn begins
  arrow: 1100, // the arrow stays up this long before the damage goes on
  phase: 900, // moving on from a phase where nothing happened
  announce: 1600, // after it announces what a card does, before its next move
  // every phase of its turn lasts at least this long (a fresh random length each phase, in ms) before it asks to
  // move on or moves on, so a quiet phase isn't over before the player has seen it
  phaseLeast: [3000, 7000] as [number, number],
  attackGap: 900, // an extra pause before each attack (on top of its step), so each can be followed; scaled by the bot's pace
};

/** What a bot does next: an action (or one worked out at the moment it is done) and how long to wait first. */
export interface BotStep {
  make: Action | ((g: Game) => Action | null);
  delay: number;
  acts?: boolean; // it counts as doing something this phase (so the bot asks before moving on)
}
export interface BotMemory {
  turn: number; // the bot's turn count when this was set up
  phase: number;
  acted: boolean; // did something this phase
  asked: boolean; // asked to move on, and is waiting (or was answered)
  queue: BotStep[];
  tried: Set<string>; // plays that were refused this turn, not tried again
  attacked: Set<string>; // cards that have attacked (or used their Tap) this turn
  announced: Set<string>; // cards whose text has been announced (for good)
  played: string | null; // the hand card just played: its effects come next
  usedAbility: boolean;
  errors: number;
  started: boolean; // the turn's first pause has been taken
  blasts: Blast[]; // Spells it cast that go off later (Timed Blast): kept from turn to turn
  possessing: { spell: string; unit: string }[]; // Possession Spells it cast, and the Unit each one holds
  playAs?: { key: string; level: BotLevel; rng?: () => number }; // how it plays this phase (its profile picks; The Forsaken plans it)
  offered?: number; // its turn count when it last offered to call the game
  answered?: string; // the request it last answered
}
/** A Spell that waits: it hits every Unit in an opposing lane on the bot's turn `due`, after its Attack Phase. */
interface Blast {
  card: string;
  lane: string; // the slot's name, on the opponent's side
  dmg: number;
  due: number;
  marker?: string; // the Target token showing the lane
  pierce?: boolean;
  direct?: boolean;
}
export function newBotMemory(): BotMemory {
  return { turn: -1, phase: -1, acted: false, asked: false, queue: [], tried: new Set(), attacked: new Set(), announced: new Set(), played: null, usedAbility: false, errors: 0, started: false, blasts: [], possessing: [] };
}
/** The server tells the bot an action of its was refused: noted, so it isn't tried again this turn. */
export function botRefused(mem: BotMemory, key: string | undefined) {
  mem.errors++;
  if (key) mem.tried.add(key);
}

// ------------------------------------------------------------------ reading the table

interface Ctx {
  g: Game;
  db: CardDb;
  tdb: TokenDb;
  seat: Seat;
  foe: Seat;
  level: BotLevel;
  rng: () => number;
}
const effectOf = (x: Ctx, t: Token) => x.tdb.byId.get(t.tokenId)?.effect ?? '';
const defOf = (x: Ctx, c: FieldCard): CardDef | undefined => (c.faceUp || c.owner === x.seat ? x.db.byNumber.get(c.num) : undefined);
const tokensOn = (x: Ctx, c: FieldCard) => {
  // the tokens on a summoned unit attached to this card are that unit's
  const kids = x.g.state.field.filter((k) => k.attachedTo === c.id);
  return tokensOnCard(c, x.g.state.tokens).filter((t) => !kids.some((k) => tokensOnCard(k, [t]).length));
};
const valueOf = (x: Ctx, c: FieldCard, effect: string) => tokensOn(x, c).filter((t) => effectOf(x, t) === effect).reduce((n, t) => n + t.value, 0);
const has = (x: Ctx, c: FieldCard, effect: string) => tokensOn(x, c).some((t) => effectOf(x, t) === effect);
const tierOf = (c: FieldCard) => 1 + Math.max(c.merged?.length ?? 0, c.swarmMerged ?? 0); // (a swarm's tier is on every unit of it)
const isUnitDef = (d?: CardDef) => !!d && (d.types.includes('troop') || d.types.includes('building'));

/** Health (or SP) a card has left, counting damage, gained Health and Overheal. */
function healthLeft(x: Ctx, c: FieldCard): number {
  const d = defOf(x, c);
  const full = d ? (formOf(d, c.form ?? 0, tierOf(c)).health ?? 0) : 10;
  const on = tokensOn(x, c);
  const lost = on.filter((t) => countsAgainstHealth(effectOf(x, t))).reduce((n, t) => n + t.value, 0);
  return full + valueOf(x, c, CONFIG.tokenRules.healthToken) + valueOf(x, c, 'overheal') - lost;
}
const destroyed = (x: Ctx, c: FieldCard) => has(x, c, CONFIG.turn.lifetime.expiredToken);
const flies = (x: Ctx, c: FieldCard) => (defOf(x, c)?.movement === 'flying' || has(x, c, 'flying')) && !has(x, c, 'ground');

/** A slot's middle on a side of the table (side 1 = seat 0's). */
const at = (slot: { x: number; z: number }, side: number) => ({ x: slot.x * side, z: slot.z * side });
const inSlot = (c: { x: number; z: number }, slot: { x: number; z: number; width: number; depth: number }, side: number) =>
  Math.abs(c.x - slot.x * side) <= slot.width / 2 && Math.abs(c.z - slot.z * side) <= slot.depth / 2;
/**
 * A slot by name. On the 2v2 table each player has their own Support, Minion and Spell slots (two of each name,
 * one on each half of the side): with a seat, the one on that player's half.
 */
const slotNamed = (name: string, seat?: Seat) => CONFIG.slots.find((s) => s.name === name && (seat === undefined || s.hand === undefined || s.hand === handOf(seat))) ?? CONFIG.slots.find((s) => s.name === name)!;
/**
 * Can this bot play into a slot? On the 2v2 table only into its own half (and its teammate's lanes once that
 * teammate is out); shared slots (the Biome) and the 1v1 table's slots, always.
 */
function openTo(x: Ctx, slot: (typeof CONFIG.slots)[number]) {
  if (!x.g.state.teams || slot.hand === undefined || slot.hand === handOf(x.seat) || CONFIG.sharedSlots.includes(slot.name)) return true;
  return slotKind(slot.name) === 'Lane' && !!x.g.state.players[(x.seat + 2) % 4]?.out;
}

/** A side's lanes, in the order they face across the table (the same index faces each other). */
function lanes(side: number) {
  return laneOrder()
    .map((slot) => ({ slot, ...at(slot, side) }))
    .sort((a, b) => a.x - b.x);
}
/** The units in a slot on a side (attached summoned units go with their card). */
function unitsIn(x: Ctx, slot: (typeof CONFIG.slots)[number], side: number) {
  // (a Calling Pillar is passed over: it can't be targeted or damaged, and it doesn't fill or block its lane)
  return x.g.state.field.filter((c) => !c.attachedTo && inSlot(c, slot, side) && !(c.faceUp && x.db.byNumber.get(c.num)?.whileInPlay.includes('untouchable')) && (!c.faceUp || isUnitDef(x.db.byNumber.get(c.num)) || isEmperor(x.db.byNumber.get(c.num)!)));
}
function emperorOf(x: Ctx, seat: Seat) {
  return x.g.state.field.find((c) => c.owner === seat && !c.spawned && isEmperor(x.db.byNumber.get(c.num)!));
}
function minionOf(x: Ctx, seat: Seat) {
  return unitsIn(x, slotNamed('Minion', seat), sideOf(seat))[0];
}

/** The damage a card deals with one hit right now (Firelance's first number while it has its Special Hit). */
function hitOf(x: Ctx, c: FieldCard, d: CardDef): number {
  const nums = numbersIn(d.damage);
  if (!nums.length) return 0;
  let dmg = nums.length > 1 && !has(x, c, 'special-hit') ? nums[nums.length - 1] : nums[0];
  if (tierOf(c) >= 3) dmg = Math.round(dmg * (1 + CONFIG.tierRules.damagePercent / 100));
  dmg += valueOf(x, c, 'damage-boost') - valueOf(x, c, 'weakness');
  return Math.max(0, dmg);
}
/** Who controls a Possessed card (the player who possessed it), or null if it isn't Possessed. */
function controllerOf(x: Ctx, c: FieldCard): Seat | null {
  const t = tokensOn(x, c).find((o) => effectOf(x, o) === 'possession');
  if (!t) return null;
  return t.by !== null && t.by !== c.owner ? t.by : c.owner === x.seat ? x.foe : x.seat;
}
const possessedByFoe = (x: Ctx, c: FieldCard) => c.owner === x.seat && controllerOf(x, c) === x.foe;
const possessedByMe = (x: Ctx, c: FieldCard) => c.owner !== x.seat && controllerOf(x, c) === x.seat;
/**
 * A card's traits right now. A trait written "A/B" is one of two attacks: a card with two damage numbers
 * ("12/3": Firelance, "7/3": Sunken Soldier) uses the first while it has its Special Hit and the second without.
 * A card with one damage number (Jungle Explorer: its Special Hit is a passive, not an attack) always uses B.
 */
function traitNow(x: Ctx, c: FieldCard, d: CardDef, word: string) {
  const special = numbersIn(d.damage).length > 1 && has(x, c, 'special-hit');
  return d.traits.some((t) =>
    t.split('|').some((part) => {
      const opts = part.split('/').map((o) => o.trim().toLowerCase());
      const now = opts.length > 1 ? (special ? opts[0] : opts[opts.length - 1]) : opts[0];
      return now === word.toLowerCase();
    }),
  );
}
/**
 * A card's range right now. A range written "1/0" (Sunken Soldier) is 1 with its Special Hit and 0 without, and
 * the Range token it brought with it is that same 1 (not counted twice); Firelance's text gives its first attack
 * 1 range. Other Range tokens add to it.
 */
function rangeNow(x: Ctx, c: FieldCard, d: CardDef) {
  const special = has(x, c, 'special-hit');
  const nums = numbersIn(d.rangeText ?? '');
  const two = nums.length > 1;
  let base = two ? (special ? nums[0] : nums[nums.length - 1]) : (d.range ?? 0);
  const m = special ? /first attack deals \d+[^.]*?has (\d+) range/i.exec(d.text) : null;
  if (m) base = Math.max(base, Number(m[1]));
  const extra = tokensOn(x, c).filter((t) => effectOf(x, t) === CONFIG.reach.rangeToken && !(two && t.from === c.id)).reduce((n, t) => n + t.value, 0);
  return Math.max(0, base + extra);
}
const trait = (d: CardDef, word: string) => d.traits.some((t) => t.split(/[\/|]/).some((s) => s.trim().toLowerCase() === word.toLowerCase()));

// ------------------------------------------------------------------ the bot's turn

/**
 * What a bot does next. `botId` is one of BOTS (or an old level name: easy, skilled, expert). Each phase it picks how
 * it plays (its profile's `play` weights), and The Forsaken first tries out several ways of playing the phase on a
 * copy of the game (planAhead) and keeps the one that leaves the table best for it.
 */
export function botNext(g: Game, db: CardDb, tdb: TokenDb, seat: Seat, botId: string, mem: BotMemory, rng: () => number = Math.random): BotStep | null {
  const prof = botProfile(botId);
  const s = g.state;
  if (s.result) return null;
  if (s.players[seat]?.out) return null; // (2v2: a bot whose Emperor has fallen only watches its teammate)
  const key = `${s.turn}:${s.players[s.turn]?.turns}:${s.phase}`;
  if (mem.playAs?.key !== key) {
    const w = prof.play; let roll = rng() * (w[0] + w[1] + w[2]);
    let level: BotLevel = 'expert';
    for (const [i, lv] of (['easy', 'skilled', 'expert'] as BotLevel[]).entries()) { roll -= w[i]; if (roll <= 0) { level = lv; break; } }
    let planRng: (() => number) | undefined;
    if (prof.lookahead && s.turn === seat && (s.phase === 1 || s.phase === 2)) {
      const best = planAhead(g, db, tdb, seat, mem, prof.lookahead);
      if (best) { level = best.level; planRng = seededRng(best.seed); }
    }
    mem.playAs = { key, level, ...(planRng ? { rng: planRng } : {}) };
  }
  const step = botNextAs(g, db, tdb, seat, mem.playAs.level, mem, mem.playAs.rng ?? rng);
  if (!step) return null;
  // its pace, and a pause between attacks long enough to follow what each one does (quicker for the stronger bots)
  const kind = typeof step.make === 'function' ? null : step.make.type;
  const attackGap = kind === 'attack' || kind === 'strike' ? BOT_PACE.attackGap : 0;
  return { ...step, delay: Math.round((step.delay + attackGap) * prof.pace) };
}
function cloneMemory(mem: BotMemory): BotMemory {
  return { ...mem, queue: [...mem.queue], tried: new Set(mem.tried), attacked: new Set(mem.attacked), announced: new Set(mem.announced), blasts: structuredClone(mem.blasts ?? []), possessing: structuredClone(mem.possessing ?? []), playAs: undefined };
}
/** How good the table is for this seat's side: a win or loss outright, Emperors' health left, the Units on each side, Gerzite and cards in hand. */
function judge(g: Game, db: CardDb, tdb: TokenDb, seat: Seat): number {
  const s = g.state;
  const mine = (o: number) => (s.teams ? o % 2 === seat % 2 : o === seat);
  if (s.result) return s.result.winner === null ? 0 : mine(s.result.winner) ? 1e5 : -1e5;
  const x: Ctx = { g, db, tdb, seat, foe: foeOf(g, seat), level: 'expert', rng: Math.random };
  let score = 0;
  for (const c of s.field) {
    const d = c.faceUp ? db.byNumber.get(c.num) : undefined;
    if (!d) continue;
    const sign = mine(c.owner) ? 1 : -1;
    if (isEmperor(d)) { const full = d.health ?? 1; score += sign * 420 * (healthLeft(x, c) / full); continue; }
    if (!isUnitDef(d) || has(x, c, CONFIG.turn.lifetime.expiredToken)) continue;
    score += sign * (healthLeft(x, c) * 0.9 + cardPower(d) * 2.2 + (Number.parseInt(d.damage ?? '', 10) || 0) * 1.4);
  }
  const bar = (o: number) => s.tokens.filter((t) => t.by === o && !t.asCounter && tdb.byId.get(t.tokenId)?.bar === 'gerzite').length;
  s.players.forEach((p, o) => { const sign = mine(o) ? 1 : -1; score += sign * (bar(o) * 0.7 + p.hand.length * 1.6); });
  return score;
}
/**
 * The Forsaken: before it plays a phase, it plays it out several ways on copies of the game (as The Creator would,
 * as Practice Buddy would, and with different choices where it has some) and keeps the way that leaves the table
 * best for it. It never looks at its opponent's hand: the copies hold it, but nothing it does there depends on it.
 */
function planAhead(g: Game, db: CardDb, tdb: TokenDb, seat: Seat, mem: BotMemory, tries: number): { level: BotLevel; seed: number } | null {
  const phase = g.state.phase;
  const options: { level: BotLevel; seed: number }[] = [];
  for (let i = 0; i < tries; i++) options.push({ level: i % 3 === 2 ? 'skilled' : 'expert', seed: 7919 * (i + 1) + g.state.version });
  let best: { level: BotLevel; seed: number } | null = null;
  let bestScore = -Infinity;
  for (const o of options) {
    const copy = g.sandbox(seat); // (blind to the opponent's face-down cards and deck order, like any player)
    (copy as unknown as { rng: () => number }).rng = seededRng(Math.floor(Math.random() * 1e9)); // (its dice roll as they would)
    const cm = cloneMemory(mem);
    const r = seededRng(o.seed);
    for (let k = 0; k < 60; k++) {
      const st = copy.state;
      if (st.result || st.turn !== seat || st.phase !== phase || st.picks?.some((p) => p.seat !== seat) || st.endRequest) break;
      const step = botNextAs(copy, db, tdb, seat, o.level, cm, r);
      if (!step) break;
      const a = typeof step.make === 'function' ? step.make(copy) : step.make;
      if (!a) continue;
      if (a.type === 'botAsk' || a.type === 'nextPhase') break; // (the phase is done)
      if (copy.apply(seat, a)) botRefused(cm, stepKey(step));
    }
    const sc = judge(copy, db, tdb, seat);
    if (sc > bestScore) { bestScore = sc; best = o; }
  }
  return best;
}
/** Each side's share of its Emperors' health left (this seat's side first), as a game called here would be decided on. */
function healthShares(x: Ctx): [number, number] {
  const s = x.g.state;
  const mine = (o: number) => (s.teams ? o % 2 === x.seat % 2 : o === x.seat);
  const side = (m: boolean) => s.players.map((_, i) => i as Seat).filter((i) => mine(i) === m);
  const share = (seats: Seat[]) => {
    let left = 0, full = 0;
    for (const i of seats) { const e = emperorOf(x, i); const d = e ? x.db.byNumber.get(e.num) : undefined; const f = d?.health ?? 1; full += f; left += e ? Math.max(0, healthLeft(x, e)) : 0; }
    return full ? left / full : 0;
  };
  return [share(side(true)), share(side(false))];
}
/**
 * Answering "End it here". A forfeit is always taken. Asked to give the game up (a claimed win), or to call it while
 * it is behind, a bot agrees only when it thinks it is losing, and the better the bot the better it judges that:
 * Martin and KeyVon guess from the Emperors' health (and often say no anyway), Practice Buddy and the like go by
 * the Emperors' health, The Grandmaster and up weigh the whole table. A call it would win, it takes.
 */
function acceptEnd(x: Ctx, kind: string): boolean {
  if (kind === 'forfeit') return true;
  const [me, them] = healthShares(x);
  if (kind === 'call' && me > them) return true; // a call it would win
  if (me <= 0) return true; // nothing left to play for
  const gap = them - me;
  if (x.level === 'easy') return gap > 0.5 && x.rng() < 0.4;
  if (x.level === 'skilled') return gap > 0.55 || (gap > 0.4 && me < 0.15);
  // expert: the whole table, Units and cards in hand included
  return gap > 0.35 && judge(x.g, x.db, x.tdb, x.seat) < -450;
}
/**
 * Offering to call it here (asking the other side to give the game up): only when the other side is clearly
 * losing, never before global turn 6, and at least 3 of its own turns after it last asked. The better the bot, the
 * surer it has to be: Martin guesses (and seldom asks), The Grandmaster and up weigh the whole table.
 */
function wantsToCall(x: Ctx, mem: BotMemory): boolean {
  const s = x.g.state;
  if (s.mode !== 'versus' || s.endRequest || x.g.globalTurns < 6) return false;
  const turns = s.players[x.seat].turns;
  if (mem.offered !== undefined && turns - mem.offered < 3) return false;
  const [me, them] = healthShares(x);
  if (me <= them) return false;
  if (x.level === 'easy') return them < 0.15 && me - them > 0.55 && x.rng() < 0.35;
  if (x.level === 'skilled') return them < 0.2 && me - them > 0.45 && x.rng() < 0.7;
  return them < 0.3 && me - them > 0.35 && judge(x.g, x.db, x.tdb, x.seat) > 450;
}
/** The opponent a seat faces: in 2v2, the one whose lanes are across from its own (the other one, once that one is out). */
function foeOf(g: Game, seat: Seat): Seat {
  const s = g.state;
  if (!s.teams) return (seat === 0 ? 1 : 0) as Seat;
  const across = (3 - seat) as Seat;
  return s.players[across]?.out ? ((across + 2) % 4) as Seat : across;
}
function botNextAs(g: Game, db: CardDb, tdb: TokenDb, seat: Seat, level: BotLevel, mem: BotMemory, rng: () => number = Math.random): BotStep | null {
  const s = g.state;
  if (s.result) return null;
  const x: Ctx = { g, db, tdb, seat, foe: foeOf(g, seat), level, rng };
  const p = s.players[seat];
  const pace = BOT_PACE.step[level];

  // ---- things to answer, whoever's turn it is
  const req = s.endRequest;
  const mineTeam = (o: number) => (s.teams ? o % 2 === seat % 2 : o === seat);
  if (req && req.from !== seat) {
    if (mineTeam(req.from)) return null; // (a teammate's request is for the other team to answer)
    if (mem.answered === `${req.from}:${req.kind}:${s.version}`) return null;
    mem.answered = `${req.from}:${req.kind}:${s.version}`;
    return { make: { type: 'answerEnd', accept: acceptEnd(x, req.kind) }, delay: 1600 };
  }
  if (req) return null; // waiting for an answer to its own request
  if (s.peekRequest && s.peekRequest.from !== seat) return { make: { type: 'peekAnswer', accept: true }, delay: 1000 };
  if (s.botAsk) return null; // waiting for a Go ahead
  if (p.overflow) return { make: { type: 'overflowChoice', to: 'bottom' }, delay: pace };
  if (p.span) {
    const starts = p.span.starts;
    return { make: { type: 'spanPlace', start: starts[Math.floor(starts.length / 2)] ?? 0 }, delay: pace };
  }
  const cure = s.tokens.find((t) => t.cure === seat);
  if (cure) return { make: { type: 'cureRoll', id: cure.id }, delay: pace };
  const roll = s.field.find((c) => c.roll === seat);
  if (roll) return { make: { type: 'cardRoll', id: roll.id }, delay: pace };
  // a choice one of its cards is waiting for (whose turn it is doesn't matter), and a Dark Bargain to agree to
  const pick = s.picks?.find((k) => k.seat === seat);
  if (pick) return { make: answerPick(x, pick), delay: pace };
  if (s.bargain && !s.bargain.agreed.includes(seat)) return { make: { type: 'bargain', deal: true }, delay: pace };
  // the game is decided on the table: the bot says so
  const mine = emperorOf(x, seat);
  const theirs = emperorOf(x, x.foe);
  // (not in 2v2: one fallen Emperor there doesn't decide the game, and the table ends it once a team has none left)
  if (!s.teams && mine && healthLeft(x, mine) <= 0) return { make: { type: 'requestEnd', kind: 'forfeit' }, delay: 1800 };
  if (!s.teams && theirs && healthLeft(x, theirs) <= 0) return { make: { type: 'requestEnd', kind: 'win' }, delay: 1800 };

  if (s.turn !== seat) return null;

  // ---- a new turn, a new phase
  if (mem.turn !== p.turns) {
    Object.assign(mem, newBotMemory(), { turn: p.turns, announced: mem.announced, blasts: mem.blasts ?? [], possessing: mem.possessing ?? [] });
  }
  if (mem.phase !== s.phase) {
    mem.phase = s.phase;
    mem.acted = false;
    mem.asked = false;
    mem.queue = [];
  }
  // the game is clearly won: now and then it offers to call it here (never before global turn 6, nor often)
  if (!mem.started && wantsToCall(x, mem)) {
    mem.offered = p.turns;
    return { make: { type: 'requestEnd', kind: 'call' }, delay: 2200 };
  }
  if (!mem.started) {
    mem.started = true;
    const e = emperorOf(x, seat);
    return { make: { type: 'ping', x: e?.x ?? 0, z: e?.z ?? 0 }, delay: BOT_PACE.turnStart }; // a small marker on its Emperor: "my turn"
  }
  if (mem.queue.length) return mem.queue.shift()!;

  // a card it just played: what it does
  if (mem.played) {
    const id = mem.played;
    mem.played = null;
    const card = s.field.find((c) => c.id === id);
    if (card) {
      resolvePlayed(x, card, mem);
      if (mem.queue.length) return mem.queue.shift()!;
    }
  }

  // tidy up: its own destroyed cards go to the discard pile (a summoned unit just goes)
  // (a waiting lane blast's timer runs out as its turn begins: it stays until it has gone off)
  const dead = s.field.find((c) => c.owner === seat && !isEmperor(db.byNumber.get(c.num)!) && destroyed(x, c) && !mem.tried.has(`discard:${c.id}`) && !waitsToBlast(x, c, mem) && !(CONFIG.combat.enabled && db.byNumber.get(c.num)!.effects.some((e) => e.conds.some((k) => k[0] === 'later' || k[0] === 'age'))));
  if (dead) {
    mem.tried.add(`discard:${dead.id}`);
    return { make: dead.spawned ? { type: 'delete', id: dead.id } : { type: 'toPile', id: dead.id, pile: 'discard', where: 'top' }, delay: pace };
  }

  // a Possession Spell whose Unit is no longer Possessed (or is gone) goes to the discard pile
  mem.possessing ??= [];
  const over = mem.possessing.find((h) => {
    const unit = s.field.find((c) => c.id === h.unit);
    return !unit || destroyed(x, unit) || controllerOf(x, unit) !== seat;
  });
  if (over) {
    mem.possessing = mem.possessing.filter((h) => h !== over);
    if (s.field.some((c) => c.id === over.spell) && !mem.tried.has(`discard:${over.spell}`)) {
      mem.tried.add(`discard:${over.spell}`);
      return { make: { type: 'toPile', id: over.spell, pile: 'discard', where: 'top' }, delay: pace };
    }
  }

  if (mem.errors > 30) return advance(x, mem); // something keeps going wrong: move the game on rather than stall

  switch (s.phase) {
    case 0:
      return overdraw(x, mem) ?? advance(x, mem);
    case 1:
      return summon(x, mem) ?? advance(x, mem);
    case 2:
      // its first turn: nothing can attack and no Spell can be cast, so the Attack Phase is skipped straight away
      if (firstTurnOf(x)) return advance(x, mem);
      return CONFIG.combat.enabled ? attackPhase(x, mem) ?? advance(x, mem) : attackPhaseManual(x, mem) ?? laneBlasts(x, mem) ?? advance(x, mem);
    default:
      return advance(x, mem);
  }
}

/** Moves to the next phase, by itself. */
/** A player's very first turn of the game: no attacks, no Spells. */
export const firstTurnOf = (x: { g: Game; seat: Seat }) => x.g.state.players[x.seat].turns === 0;

function advance(x: Ctx, mem: BotMemory): BotStep {
  const s = x.g.state;
  // its first turn: no attacks, so once its summons are done it goes straight through the Attack Phase
  if (firstTurnOf(x) && s.phase >= 1) return { make: { type: 'nextPhase' }, delay: mem.acted ? BOT_PACE.phase : 300 };
  // it ends its turn by itself: the table carries out its cards' effects, so there is nothing to wait on
  if (s.phase === 2) return { make: { type: 'nextPhase' }, delay: BOT_PACE.phase };
  // (it never asks to move on: the table carries out its cards, so there is nothing for its opponent to settle)
  return { make: { type: 'nextPhase' }, delay: mem.acted ? 500 : BOT_PACE.phase };
}

// ---- Overdraw Phase: draw an extra card when the hand is thin and there is Gerzite to spare
function overdraw(x: Ctx, mem: BotMemory): BotStep | null {
  if (x.level === 'easy' || mem.tried.has('overdraw')) return null;
  const v = x.g.view(x.seat);
  const hand = x.g.state.players[x.seat].hand.length;
  const spare = v.players[x.seat].gerzite;
  if (hand <= (x.level === 'expert' ? 3 : 2) && spare >= 6 && x.g.state.players[x.seat].deck.length > 5) {
    mem.tried.add('overdraw');
    return { make: { type: 'draw', pay: true }, delay: BOT_PACE.step[x.level] };
  }
  return null;
}

// ---- the secret rares: Calling Pillars, Realm Beyond Comprehension, The Remaining
/**
 * What a bot holding these cards does about them in its Summon Phase: a step to take, 'wait' (do nothing else
 * this phase: it is saving up), or null (nothing to do about them: play as usual).
 *   Creator works at it: it gets Realm Beyond Comprehension into play first (sacrificing units for the Dark
 *   Gerzite if it must), puts a Pillar in every lane, holding the last one back until the Realm is up, then saves
 *   its Gerzite for The Remaining and summons it.
 *   Practice Buddy only dabbles: it puts Pillars down and plays the other two when it happens to be able to.
 */
function callingPlan(x: Ctx, mem: BotMemory, gz: number, dark: number): BotStep | 'wait' | null {
  if (x.level === 'easy') return null;
  const s = x.g.state;
  const p = s.players[x.seat];
  const side = sideOf(x.seat);
  const tagged = (num: number, tag: string) => !!x.db.byNumber.get(num)?.whileInPlay.includes(tag);
  const inHand = (tag: string) => p.hand.find((c) => tagged(c.num, tag));
  const pillar = inHand('calling');
  const realm = inHand('remaining_gate');
  const remaining = inHand('jumps');
  if (!pillar && !realm && !remaining) return null;
  const hard = x.level === 'expert';
  const step = (make: Action, key: string): BotStep | null => {
    if (mem.tried.has(key)) return null;
    mem.acted = true;
    return { make, delay: BOT_PACE.step[x.level], acts: true, ...{ key } } as BotStep;
  };
  const myLanes = lanes(side);
  const gate = s.field.some((c) => c.faceUp && tagged(c.num, 'remaining_gate') && !destroyed(x, c));
  const biome = slotNamed('Biome');
  const biomeFree = !s.field.some((c) => inSlot(c, biome, 1));
  // a unit of its own it could give up for Dark Gerzite, or to make room: the weakest that isn't still Entrenched
  const spare = () =>
    s.field
      .filter((c) => c.owner === x.seat && c.faceUp && !c.spawned && !destroyed(x, c) && isUnitDef(x.db.byNumber.get(c.num)) && !tagged(c.num, 'untouchable') && !has(x, c, CONFIG.tokenRules.entrenchedToken) && !mem.tried.has(`sacrifice:${c.id}`))
      .sort((a, b) => cardPower(x.db.byNumber.get(a.num)!) - cardPower(x.db.byNumber.get(b.num)!))[0];

  // The Remaining is in its hand: everything goes into summoning it
  if (remaining) {
    const cost = x.db.byNumber.get(remaining.num)!.cost ?? 0;
    if (gate) {
      if (gz < cost) return hard ? 'wait' : null; // Creator spends nothing else until it can pay
      const free = myLanes.find((l) => openTo(x, l.slot) && !unitsIn(x, l.slot, side).length);
      if (free) {
        const go = step({ type: 'play', id: remaining.id, x: free.x, z: free.z, faceDown: false }, `play:${remaining.id}:lane`);
        if (go) { mem.played = remaining.id; return go; }
      } else if (hard) {
        const out = spare();
        if (out) return step({ type: 'sacrifice', id: out.id }, `sacrifice:${out.id}`); // its lanes are full: one unit makes way
      }
      return null;
    }
  }
  // Realm Beyond Comprehension: into play as soon as it can pay for it
  if (realm && !gate && biomeFree) {
    const cost = x.db.byNumber.get(realm.num)!.cost ?? 0;
    if (dark >= cost) {
      const go = step({ type: 'play', id: realm.id, x: biome.x, z: biome.z, faceDown: false }, `play:${realm.id}:biome`);
      if (go) { mem.played = realm.id; return go; }
    } else if (hard && (remaining || s.field.some((c) => c.owner === x.seat && c.faceUp && tagged(c.num, 'calling')))) {
      // Creator, once its plan is under way, gives up units for the Dark Gerzite
      const out = spare();
      if (out) return step({ type: 'sacrifice', id: out.id }, `sacrifice:${out.id}`);
    }
  }
  // Calling Pillars: one into each lane that has none. Not once The Remaining has already been called.
  const stillToCall = [...p.deck, ...p.discard].some((c) => tagged(c.num, 'jumps'));
  if (pillar && stillToCall) {
    const cost = x.db.byNumber.get(pillar.num)!.cost ?? 0;
    // (a Pillar may go in a teammate's lanes too: on the 2v2 table the call takes all four of the side's lanes, and a
    // fifth Pillar doubled up in one of them, its own first)
    const mine = s.field.filter((c) => c.owner === x.seat && c.faceUp && tagged(c.num, 'calling') && myLanes.some((l) => inSlot(c, l.slot, side)));
    const missing = myLanes.filter((l) => !mine.some((c) => inSlot(c, l.slot, side)));
    const need = Math.max(myLanes.length, CONFIG.secretRares.callingPillars);
    const toGo = Math.max(missing.length, need - mine.length);
    const places = missing.length ? missing : toGo > 0 ? myLanes.filter((l) => openTo(x, l.slot)) : [];
    // the last one sets the call off and costs it its hand: Creator holds it until the Realm is in play, as long
    // as there is still a Realm to wait for
    const realmToCome = !!realm || p.deck.some((c) => tagged(c.num, 'remaining_gate'));
    const hold = hard && toGo === 1 && !gate && realmToCome;
    if (places.length && gz >= cost && !hold) {
      const l = places[Math.floor(x.rng() * places.length)];
      const go = step({ type: 'play', id: pillar.id, x: l.x, z: l.z, faceDown: false }, `play:${pillar.id}:pillar`);
      if (go) { mem.played = pillar.id; return go; }
    }
  }
  return null;
}

// ---- Summon Phase: play cards from the hand
function summon(x: Ctx, mem: BotMemory): BotStep | null {
  const s = x.g.state;
  const p = s.players[x.seat];
  const v = x.g.view(x.seat);
  const gz = v.players[x.seat].gerzite;
  const dark = v.players[x.seat].darkGerzite;
  const firstTurn = p.turns === 0;
  const side = sideOf(x.seat);
  // the secret rares come first: a bot that has them follows its plan for them before anything else
  const plan = callingPlan(x, mem, gz, dark);
  if (plan === 'wait') return null;
  if (plan) return plan;
  const options: { card: (typeof p.hand)[number]; def: CardDef; where: { x: number; z: number }; score: number; key: string; extra?: { choice?: number; unit?: string } }[] = [];
  const myLanes = lanes(side);
  const foeLanes = lanes(-side);
  // (Vast Expanse: each lane holds two cards)
  const freeLanes = myLanes.map((l, i) => ({ ...l, i })).filter((l) => openTo(x, l.slot) && (v.wideLanes ? unitsIn(x, l.slot, side).filter((c) => !c.spawned).length < 2 : !unitsIn(x, l.slot, side).length));
  const minionFree = !minionOf(x, x.seat);
  const spellSlots = CONFIG.slots.filter((sl) => slotKind(sl.name) === 'Spell' && openTo(x, sl) && !x.g.state.field.some((c) => inSlot(c, sl, side)));
  const support = slotNamed('Support', x.seat);
  const supportFree = !s.field.some((c) => inSlot(c, support, side));
  const biome = slotNamed('Biome');
  const biomeFree = !s.field.some((c) => inSlot(c, biome, 1));
  const empty = foeLanes.map((l) => !unitsIn(x, l.slot, -side).length);
  // a Lock on one of its own cards (or one it is the one to pay off): paid off when it can spare the Gerzite and the
  // card is worth it (Martin only now and then)
  for (const t of s.tokens) {
    if (effectOf(x, t) !== CONFIG.tokenRules.lockToken || !(t.price && t.price > 0)) continue;
    const on = s.field.find((c) => (t.ability ? c.id === t.ability.card : tokensOn(x, c).includes(t)));
    const payer = t.payBy !== undefined ? t.payBy : on?.owner;
    if (payer === undefined || (s.teams ? payer % 2 !== x.seat % 2 : payer !== x.seat)) continue;
    const key = `payLock:${t.id}`;
    if (mem.tried.has(key)) continue;
    const have = t.priceDark ? dark : gz;
    const d = on ? defOf(x, on) : undefined;
    const worth = d ? (isEmperor(d) ? 8 : cardPower(d)) : 0;
    const keep = x.level === 'expert' ? 2 : 0; // (Creator keeps a little back for its turn)
    if (have - keep < t.price || worth < t.price * 0.8 || (x.level === 'easy' && x.rng() < 0.5)) continue;
    mem.acted = true;
    return { make: { type: 'payLock', id: t.id }, delay: BOT_PACE.step[x.level], acts: true, ...{ key } } as BotStep;
  }
  // a Unit a "unit + 1" Spell paid for (Tribunal Trigger): it goes down face-down into one of its lanes, for free
  if (p.freeUnit) {
    const held = p.hand.find((c) => c.id === p.freeUnit!.id);
    const lane = freeLanes[0];
    const key = `play:${p.freeUnit.id}:pledged`;
    if (held && lane && !mem.tried.has(key)) {
      mem.acted = true;
      mem.played = held.id;
      return { make: { type: 'play', id: held.id, x: lane.x, z: lane.z, faceDown: true }, delay: BOT_PACE.step[x.level], acts: true, ...{ key } } as BotStep;
    }
  }
  // the most expensive good card it could afford next turn: worth saving for (expert)
  const nextTurn = gz + 3;
  const bigHope = x.level === 'expert' ? Math.max(0, ...p.hand.map((h) => x.db.byNumber.get(h.num)!).filter((d) => isUnitDef(d) && (d.cost ?? 99) > gz && (d.cost ?? 99) <= nextTurn).map((d) => cardPower(d) * (d.cost ?? 0))) : 0;

  let threatCache: number | null = null;
  const threatNow = () => (threatCache ??= threat(x, x.g));
  for (const card of p.hand) {
    const def = x.db.byNumber.get(card.num);
    if (!def || isSecretRare(def)) continue; // (the secret rares are played by callingPlan only)
    if (def.costFormula) {
      // a Spell whose cost is worked out as it is played (Gerzite Dagger, Bloodburst, Primordial Seastorm…): only
      // the strongest bots play these. Each way of paying is tried on a copy of the table, and what it spends is
      // weighed against what it does.
      if (x.level !== 'expert' || !CONFIG.combat.enabled || !def.types.includes('spell') || firstTurn || !spellSlots.length) continue;
      const key = `play:${card.id}:spell`;
      if (mem.tried.has(key)) continue;
      const where = at(spellSlots[0], side);
      const f = def.costFormula;
      const ways: { choice?: number; unit?: string }[] = f.uses.includes('unit')
        ? p.hand.filter((h) => h.id !== card.id && isUnitDef(x.db.byNumber.get(h.num))).map((h) => ({ unit: h.id }))
        : f.choice ? [1, 2, 3, 4, 5, 6, 8, 10].map((n) => ({ choice: n })) : [{}];
      let best: { v: number; extra: { choice?: number; unit?: string } } | null = null;
      for (const extra of ways) {
        const r = tryOn(x, { type: 'play', id: card.id, x: where.x, z: where.z, faceDown: false, ...extra });
        if (!r) continue;
        if (extra.unit) {
          // (Tribunal Trigger: the Unit it pays for then goes down for free: that is what the Spell is worth)
          const lane = freeLanes[0];
          const before = snap(x, x.g);
          if (!lane || r.g.apply(x.seat, { type: 'play', id: extra.unit, x: lane.x, z: lane.z, faceDown: true })) continue;
          r.v = weigh(x, before, r.g) + cardPower(x.db.byNumber.get(p.hand.find((h) => h.id === extra.unit)!.num)!) * 0.5;
        }
        const after = r.g.view(x.seat).players[x.seat];
        const spent = gz - after.gerzite + (dark - after.darkGerzite);
        const v = r.v - spent * 1.5;
        if (!best || v > best.v) best = { v, extra };
      }
      if (best && best.v > 2) options.push({ card, def, where, score: cardPower(def) + best.v * 0.3, key, extra: best.extra });
      continue;
    }
    const cost = def.cost ?? 0;
    if (def.costDark ? cost > dark : cost > gz) continue;
    const power = cardPower(def);
    const base = x.level === 'easy' ? x.rng() * 10 : power * (1 + cost / 4) + x.rng() * (x.level === 'skilled' ? 2 : 0.5);
    const add = (where: { x: number; z: number }, score: number, tag: string) => {
      const key = `play:${card.id}:${tag}`;
      if (!mem.tried.has(key)) options.push({ card, def, where, score, key });
    };
    if (isUnitDef(def)) {
      for (const l of freeLanes) {
        // a lane facing an enemy unit blocks it; one facing an empty enemy lane leaves a Direct path open
        const facing = unitsIn(x, foeLanes[l.i].slot, -side)[0];
        const facingDef = facing ? defOf(x, facing) : undefined;
        let lane = 0;
        if (x.level !== 'easy') {
          if (facing) lane += 3 + (facingDef ? Math.max(0, ...numbersIn(facingDef.damage)) * 0.4 : 1);
          if (!facing && trait(def, 'Direct')) lane += 4; // straight at the Emperor
          lane += (2 - Math.abs(l.i - 2)) * 0.3; // the middle lanes reach more
        }
        if (CONFIG.combat.enabled && x.level === 'expert') {
          // Creator tries each lane on a copy: what the card does as it arrives, and what it would face next turn there
          const r = tryOn(x, { type: 'play', id: card.id, x: l.x, z: l.z, faceDown: false });
          if (r) lane = (r.v - THREAT * (threat(x, r.g) - threatNow())) * 0.5 + (2 - Math.abs(l.i - 2)) * 0.3;
        }
        add({ x: l.x, z: l.z }, base + lane + x.rng(), `lane${l.i}`);
      }
      if (minionFree && def.types.includes('troop') && !freeLanes.length && x.level !== 'easy') add(at(slotNamed('Minion', x.seat), side), base - 2, 'minion');
      if (minionFree && def.types.includes('troop') && x.level === 'easy' && x.rng() < 0.15) add(at(slotNamed('Minion', x.seat), side), base, 'minion');
    } else if (def.types.includes('spell')) {
      if (firstTurn || !spellSlots.length) continue;
      const where = at(spellSlots[0], side);
      if (CONFIG.combat.enabled && x.level !== 'easy') {
        // tried on a copy first: a Spell that would hurt the bot more than its opponent waits; one that does a lot comes first
        const tried = trial(x, { type: 'play', id: card.id, x: where.x, z: where.z, faceDown: false });
        // (what the Spell costs is weighed against what it does: a big Spell for a small result can wait)
        // (one whose effect doesn't show yet, such as Timed Blast going off next turn, isn't charged for it here)
        const v = tried === null ? null : Math.abs(tried) < 0.5 ? tried : tried - cost * (x.level === 'expert' ? 1.1 : 0.7);
        if (v === null || v < (x.level === 'expert' ? 0 : -3)) continue;
        add(where, base + v * 0.3 - 1, 'spell');
        continue;
      }
      const target = bestSpellTarget(x, def);
      if (!target && numbersIn(def.damage).length && !def.text) continue; // a damage Spell with nothing to hit
      add(where, base + (target ? target.score * 0.3 : 0) - 1, 'spell');
    } else if (def.types.includes('biome')) {
      if (!biomeFree || x.level === 'easy' && x.rng() < 0.5) continue;
      const mineAff = new Set(x.db.byNumber.get(emperorOf(x, x.seat)!.num)!.affiliations);
      const fits = def.affiliations.some((a) => mineAff.has(a) || a === 'neutral');
      if (x.level !== 'easy' && !fits) continue;
      add(at(biome, 1), base - 1, 'biome');
    } else if (def.types.includes('tome') || def.types.includes('trigger')) {
      if (!supportFree) continue;
      add(at(support, side), base - (def.types.includes('trigger') ? 2 : 1), 'support');
    }
  }
  // tier up: a copy in the hand onto the same card in play (skilled and expert)
  if (x.level !== 'easy') {
    for (const card of p.hand) {
      const def = x.db.byNumber.get(card.num);
      const onTable = s.field.find((c) => c.owner === x.seat && c.num === card.num && c.faceUp && !c.clone && !destroyed(x, c) && tierOf(c) < (def?.tiers ?? 1) && !x.g.tierRefusal(c)); // (not one that is hurt, afflicted, or a swarm missing a unit)
      const key = `merge:${card.id}`;
      if (def && onTable && !mem.tried.has(key) && (def.cost ?? 99) <= gz) options.push({ card, def, where: onTable, score: cardPower(def) * 2 + 4, key });
    }
  }
  if (!options.length) return null;
  // easy plays only some of what it could; the others don't throw cheap cards away when a big one is a turn off
  options.sort((a, b) => b.score - a.score);
  let pick = options[0];
  if (x.level === 'easy' && x.rng() < 0.25) return null;
  if (bigHope && pick.def.cost !== null && cardPower(pick.def) * pick.def.cost < bigHope * 0.5 && gz - pick.def.cost < (nextTurn - 3)) {
    // saving up: only if it leaves too little for the big card next turn
    const after = gz - pick.def.cost + 3;
    const need = Math.min(...p.hand.map((h) => x.db.byNumber.get(h.num)!).filter((d) => isUnitDef(d) && (d.cost ?? 99) > gz).map((d) => d.cost ?? 99));
    if (after < need) return null;
  }
  if (empty.every(Boolean) && pick.def.types.includes('spell') && options.some((o) => isUnitDef(o.def))) pick = options.find((o) => isUnitDef(o.def))!; // nothing to hit: units first
  mem.acted = true;
  if (pick.key.startsWith('merge:')) {
    const into = pick.where as FieldCard;
    return { make: { type: 'merge', id: pick.card.id, into: into.id }, delay: BOT_PACE.step[x.level], acts: true, ...{ key: pick.key } } as BotStep;
  }
  mem.played = pick.card.id;
  const faceDown = pick.def.types.includes('trigger');
  return { make: { type: 'play', id: pick.card.id, x: pick.where.x, z: pick.where.z, faceDown, ...(pick.extra ?? {}) }, delay: BOT_PACE.step[x.level], acts: true, ...{ key: pick.key } } as BotStep;
}

/** After a card is played face-up: what its text says, and what the bot can do of it. */
function resolvePlayed(x: Ctx, card: FieldCard, mem: BotMemory) {
  const def = x.db.byNumber.get(card.num);
  if (!def || !card.faceUp) return;
  const name = def.name;
  // (the table carries out what a card does by itself: nothing more from the bot)
  if (CONFIG.combat.enabled) return;
  if (def.types.includes('spell')) {
    const target = bestSpellTarget(x, def);
    const dmg = Math.max(0, ...numbersIn(def.damage), 0);
    const heals = Math.max(0, ...numbersIn(def.heals), 0);
    // the bot deals a Spell's damage itself only when the Spell simply does that now; one that waits, or
    // aims at the bot's own units, or works some other way, is announced for the players to settle
    const simple = /\bdeal\s+\d+/i.test(def.text) && !LATER.test(def.text);
    // "Choose an opposing lane… during your next turn, after your Attack Phase ends, deal 15 splash damage to
    // all Units within that targeted lane": it picks the lane now, marks it, and does the damage next turn
    // "Possess an opposing Unit for two turns": the best of its opponent's Units is the bot's to use
    const possess = /possess an opposing unit for (\w+) turns?/i.exec(def.text);
    if (possess) {
      const unit = bestToPossess(x, null);
      if (unit) {
        const words: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5 };
        const n = words[possess[1].toLowerCase()] ?? (Number(possess[1]) || 1);
        mem.queue.push({ make: { type: 'attack', from: card.id, to: unit.id, effect: true }, delay: BOT_PACE.step[x.level], acts: true });
        mem.queue.push(...tokenSteps(x, 'possession', unit, n, BOT_PACE.arrow));
        (mem.possessing ??= []).push({ spell: card.id, unit: unit.id });
      }
      if (def.text) mem.queue.push(announce(x, `${name} — ${def.text}`, card.id));
      if (!unit) mem.queue.push({ make: { type: 'toPile', id: card.id, pile: 'discard', where: 'top' }, delay: BOT_PACE.announce });
      return;
    }
    const later = delayedLaneBlast(def);
    if (later) {
      planBlast(x, card, def, later, mem);
      if (def.text) mem.queue.push(announce(x, `${name} — ${def.text}`, card.id));
      return;
    }
    if (target && dmg > 0 && simple) {
      mem.queue.push({ make: { type: 'attack', from: card.id, to: target.cards[0].id, effect: true }, delay: BOT_PACE.step[x.level], acts: true });
      for (const t of target.cards) mem.queue.push(...damageSteps(x, t, dmg, { spell: true, pierce: trait(def, 'Pierce'), direct: trait(def, 'Direct') }));
      statusFromText(x, def, target.cards[0], mem);
    }
    // "…and an additional 2 damage to the opposing Emperor" (Spectral Harpoon), unless the text spares an
    // Emperor at or below some Health
    const extra = /additional\s+(\d+)\s+damage to the opposing emperor/i.exec(def.text);
    const foeEmp = emperorOf(x, x.foe);
    if (extra && simple && foeEmp) {
      const spare = /cannot damage emperors? if (?:they are|it is) at or below (\d+) health/i.exec(def.text);
      if (!spare || healthLeft(x, foeEmp) > Number(spare[1])) {
        if (!target) mem.queue.push({ make: { type: 'attack', from: card.id, to: foeEmp.id, effect: true }, delay: BOT_PACE.step[x.level], acts: true });
        mem.queue.push(...damageSteps(x, foeEmp, Number(extra[1]), { spell: true, pierce: trait(def, 'Pierce'), direct: trait(def, 'Direct'), firstDelay: 450 }));
      }
    }
    if (!(target && dmg > 0 && simple) && heals > 0) {
      const ally = mostHurt(x);
      if (ally) mem.queue.push(...healSteps(x, ally, heals));
    }
    if (def.text) mem.queue.push(announce(x, `${name} — ${def.text}`, card.id));
    if (!LATER.test(def.text)) summonFromText(x, card, def, mem);
    // a Spell that is over once it has done its work goes to the discard pile
    // (Magical Coin waits for its die roll and goes to the discard pile by itself)
    if (def.turns === null && !def.duration && !def.ends.length && !def.onPlay.includes('coin_gerzite') && !LATER.test(def.text)) mem.queue.push({ make: { type: 'toPile', id: card.id, pile: 'discard', where: 'top' }, delay: BOT_PACE.announce });
    return;
  }
  // (an Entrance the table carries out by itself, like Cosmic Beyonder's, needs no settling)
  if (def.text && !mem.announced.has(card.id) && !def.onPlay.includes('discard_recycle')) {
    mem.announced.add(card.id);
    mem.queue.push(announce(x, `${name} — ${def.text}`, card.id));
    if (def.abilities.includes('entrance')) summonFromText(x, card, def, mem);
  }
}

/** A Spell whose text aims at an opposing lane now and deals damage to every Unit in it on the next turn. */
function delayedLaneBlast(def: CardDef): number | null {
  if (CONFIG.combat.enabled && def.effects.length) return null; // the table does it by itself
  if (!/choose an opposing lane/i.test(def.text) || !/next turn/i.test(def.text)) return null;
  const m = /deal\s+(\d+)\s+(?:splash\s+)?damage to all units (?:with)?in that (?:targeted )?lane/i.exec(def.text);
  return m ? Number(m[1]) : null;
}
const foeLaneNamed = (x: Ctx, name: string) => lanes(-sideOf(x.seat)).find((l) => l.slot.name === name);
/** A card the bot holds back from the discard pile: a lane blast that hasn't gone off yet. */
function waitsToBlast(x: Ctx, c: FieldCard, mem: BotMemory) {
  if (mem.blasts?.some((b) => b.card === c.id)) return true;
  const d = defOf(x, c);
  return !!d && c.faceUp && delayedLaneBlast(d) !== null && !mem.tried.has(`blast:${c.id}`);
}
/** Picks the opposing lane worth the most to hit, remembers it, and marks it with a Target token. */
function planBlast(x: Ctx, card: FieldCard, def: CardDef, dmg: number, mem: BotMemory) {
  const side = sideOf(x.seat);
  const best = bestSpellTarget(x, { ...def, traits: [...def.traits, 'Splash Damage'] });
  const foeLanes = lanes(-side);
  const lane = (best && foeLanes.find((l) => best.cards.some((c) => inSlot(c, l.slot, -side)))) ?? foeLanes[Math.floor(x.rng() * foeLanes.length)] ?? foeLanes[0];
  if (!lane) return;
  const blast: Blast = { card: card.id, lane: lane.slot.name, dmg, due: x.g.state.players[x.seat].turns + 1, pierce: trait(def, 'Pierce'), direct: trait(def, 'Direct') };
  mem.blasts = [...(mem.blasts ?? []).filter((b) => b.card !== card.id), blast];
  mem.queue.push({ make: { type: 'attack', from: card.id, x: lane.x, z: lane.z, effect: true }, delay: BOT_PACE.step[x.level], acts: true });
  if (x.tdb.byId.get('target')) {
    // the token sits at the lane's back edge, so it reads as "this lane" and not as a mark on one Unit
    const spot = { x: lane.x, z: lane.z - Math.sign(lane.z || -side) * (lane.slot.depth / 2 - 1.5) };
    mem.queue.push({ make: { type: 'spawnToken', tokenId: 'target', at: spot }, delay: BOT_PACE.step[x.level], acts: true });
    mem.queue.push({
      make: (g) => {
        const t = g.state.tokens.at(-1);
        if (t?.tokenId !== 'target') return null;
        blast.marker = t.id;
        return { type: 'move', id: t.id, ...spot }; // it is set down clear of the cards: onto the lane
      },
      delay: 350,
    });
  }
}
/**
 * After its Attack Phase on the turn they are due: each lane blast deals its damage to every Unit then in its
 * lane (Splash: no miss), its Target token comes off, and the Spell goes to the discard pile. A blast that was
 * forgotten (the server restarted) goes off at the first lane worth hitting.
 */
function laneBlasts(x: Ctx, mem: BotMemory): BotStep | null {
  const s = x.g.state;
  const turns = s.players[x.seat].turns;
  mem.blasts ??= [];
  for (const c of s.field) {
    if (c.owner !== x.seat || !c.faceUp || mem.blasts.some((b) => b.card === c.id) || mem.tried.has(`blast:${c.id}`)) continue;
    const d = defOf(x, c);
    const dmg = d && delayedLaneBlast(d);
    if (!dmg || !destroyed(x, c)) continue; // still on its first turn: its timer hasn't run out
    const best = bestSpellTarget(x, { ...d, traits: [...d.traits, 'Splash Damage'] });
    const side = sideOf(x.seat);
    const lane = lanes(-side).find((l) => best?.cards.some((u) => inSlot(u, l.slot, -side)));
    mem.blasts.push({ card: c.id, lane: lane?.slot.name ?? '', dmg, due: turns });
  }
  const i = mem.blasts.findIndex((b) => b.due <= turns);
  if (i < 0) return null;
  const b = mem.blasts.splice(i, 1)[0];
  mem.tried.add(`blast:${b.card}`);
  const card = s.field.find((c) => c.id === b.card);
  const lane = foeLaneNamed(x, b.lane);
  const steps: BotStep[] = [];
  if (card && lane) {
    const side = sideOf(x.seat);
    const hit = unitsIn(x, lane.slot, -side).filter((u) => !destroyed(x, u));
    steps.push({ make: { type: 'attack', from: card.id, x: lane.x, z: lane.z, effect: true }, delay: BOT_PACE.step[x.level], acts: true });
    for (const u of hit) steps.push(...damageSteps(x, u, b.dmg, { spell: true, pierce: b.pierce, direct: b.direct }));
    const name = x.db.byNumber.get(card.num)?.name ?? 'The Spell';
    steps.push(announce(x, `${name} goes off: ${b.dmg} splash damage to ${hit.length ? `every Unit in ${b.lane}` : `${b.lane}, which is empty`}.`));
  }
  if (b.marker) {
    const id = b.marker;
    steps.push({ make: (g) => (g.state.tokens.some((t) => t.id === id) ? { type: 'delete', id } : null), delay: 400 });
  }
  if (card) steps.push({ make: (g) => (g.state.field.some((c) => c.id === b.card) ? { type: 'toPile', id: b.card, pile: 'discard', where: 'top' } : null), delay: BOT_PACE.announce });
  if (!steps.length) return laneBlasts(x, mem);
  mem.queue.push(...steps.slice(1));
  return steps[0];
}

/** Text that means a card's effect happens some other time, or to the bot's own side: the bot doesn't carry it out. */
const LATER = /(next turn|wait|after your opponent|at the (beginning|start|end) of|choose a unit you control|unit you control|your units?\b|until)/i;

function announce(x: Ctx, text: string, card?: string): BotStep {
  return { make: { type: 'announce', text, ...(card ? { card } : {}) }, delay: BOT_PACE.announce, acts: true };
}

// ---- Attack Phase, on the automatic table

/** How much a card is worth keeping (or taking away): what it cost, and how hard it hits. */
function worthOf(x: Ctx, c: FieldCard): number {
  const d = x.db.byNumber.get(c.num);
  return (d?.cost ?? 3) + (Number.parseInt(d?.damage ?? '', 10) || 0) * 1.5 + 2;
}
type Snap = Map<string, { owner: Seat; health: number; worth: number; emperor: boolean; marks: number }>;
// statuses that hold a card back or hurt it, and those that help it (how much a card is marked, for better or worse)
const BAD = ['bleed', 'burn', 'frozen', 'stunned', 'weakness', 'susceptible', 'decay', 'locked', 'comatose', 'crumble', 'possession'];
const GOOD = ['overheal', 'overclock', 'damage-boost', 'divinity', 'shield', 'buffer', 'regeneration', 'frenzy', 'thorns', 'overshield', 'ambush'];
/**
 * How much the statuses on a card help it (above 0) or hold it back (below 0), weighed by what the card does: a
 * Stun or a Freeze on a Unit that hits hard costs its side a whole attack (or two), the same on a Skell-Mini very
 * little; damage over time and weakening by their amount; the helpful ones the same way.
 */
function marksOf(x: Ctx, g: Game, c: FieldCard): number {
  const d = c.faceUp ? x.db.byNumber.get(c.num) : undefined;
  const hits = d ? hitOf(x, c, d) * (has({ ...x, g }, c, 'frenzy') ? 2 : 1) : 2;
  const attack = hits + 1.5; // (what one of its attacks is worth to its side)
  let n = 0;
  for (const t of tokensOnCard(c, g.state.tokens)) {
    const e = x.tdb.byId.get(t.tokenId)?.effect ?? '';
    const amt = Math.min(5, Math.max(1, t.value));
    switch (e) {
      case 'stunned': case 'frozen': case 'comatose': n -= attack * Math.min(2, Math.max(1, t.turns ?? t.value ?? 1)); break;
      case 'possession': n -= attack * 1.5 + 3; break;
      case 'locked': n -= attack * 1.2; break;
      case 'burn': case 'bleed': case 'decay': n -= 1.4 * amt; break;
      case 'weakness': n -= Math.min(hits, amt) * 1.3; break;
      case 'susceptible': n -= amt * 1.2; break;
      case 'crumble': n -= 1.5; break;
      case 'shield': case 'overshield': case 'buffer': n += 1.5 * amt; break;
      case 'damage-boost': case 'overclock': n += 1.2 * amt; break;
      case 'divinity': n += 4; break;
      case 'frenzy': n += Math.max(1, hits / 2); break;
      case 'overheal': case 'regeneration': case 'thorns': n += amt; break;
      case 'ambush': n += 1; break;
      default: {
        // (anything else: the old rule of thumb, by the lists)
        const k = 1 + Math.min(4, Math.max(0, t.value - 1)) * 0.4;
        if (BAD.includes(e)) n -= k;
        else if (GOOD.includes(e)) n += k;
      }
    }
  }
  return n;
}
/** The Units and Emperors on a table, with the health each has left. */
function snap(x: Ctx, g: Game): Snap {
  const out: Snap = new Map();
  for (const c of g.state.field) {
    const d = c.faceUp ? x.db.byNumber.get(c.num) : undefined;
    if (!d || !(isUnitDef(d) || isEmperor(d))) continue;
    out.set(c.id, { owner: c.owner, health: g.healthOf(c.id), worth: worthOf(x, c), emperor: isEmperor(d), marks: marksOf(x, g, c) });
  }
  return out;
}
/**
 * How good a change on the table is for the bot: what it destroyed and damaged on its opponent's side, less what
 * it lost on its own; finishing the opposing Emperor beats everything, losing its own is the worst.
 */
function weigh(x: Ctx, before: Snap, g: Game): number {
  let v = 0;
  for (const [id, b] of before) {
    const c = g.state.field.find((f) => f.id === id);
    const gone = !c || has({ ...x, g }, c, CONFIG.turn.lifetime.expiredToken);
    const now = gone ? 0 : g.healthOf(id);
    const lost = b.health - now;
    const sign = b.owner === x.seat ? -1 : 1;
    if (b.emperor) v += sign * (now <= 0 ? 10000 : lost * (x.level === 'expert' ? 4 : 2.5)); // (Creator races harder)
    // (a kill is worth what the card was worth, and a little more: not a flat bonus, so a 1-Gerzite Skell-Mini isn't
    // worth spending a big Spell on just because it dies)
    else v += sign * (gone ? b.worth * 1.6 + 4 : (lost / Math.max(1, b.health)) * b.worth);
    if (!gone && c) v -= sign * (marksOf(x, g, c) - b.marks) * 2.2; // statuses put on (bad ones on theirs, good ones on its own), weighed by what they hold back
  }
  // Units it gained (summoned, or a Unit it now holds)
  for (const c of g.state.field) if (!before.has(c.id) && c.owner === x.seat && c.faceUp && isUnitDef(x.db.byNumber.get(c.num))) v += worthOf(x, c) * 0.5;
  return v;
}
/** Tries a move on a copy of the game (answering what it asks the bot along the way): how good it is, or null if it can't be made. */
function trial(x: Ctx, action: Action): number | null {
  return tryOn(x, action)?.v ?? null;
}
function tryOn(x: Ctx, action: Action): { v: number; g: Game } | null {
  const sb = x.g.sandbox(x.seat); // (blind to what it can't know: the opponent's face-down cards, their deck's order)
  const before = snap(x, sb);
  if (sb.apply(x.seat, action)) return null;
  for (let i = 0; i < 4; i++) {
    const k = sb.state.picks?.find((q) => q.seat === x.seat);
    if (!k || sb.apply(x.seat, answerPick({ ...x, g: sb, rng: () => 0.5 }, k))) break;
  }
  return { v: weigh(x, before, sb), g: sb };
}
/** How much it would cost Creator to look ahead too far: what the opponent's attacks could do next turn, weighed. */
const THREAT = 0.3;
/**
 * What the opponent could do to the bot in their next Attack Phase on this table (Creator looks ahead): each of
 * their cards making its best attack, weighed the way the bot weighs its own.
 */
function threat(x: Ctx, g: Game): number {
  const best = new Map<string, number>();
  for (const a of g.nextAttacks(x.foe)) {
    const t = g.state.field.find((c) => c.id === a.to);
    if (!t || t.owner !== x.seat) continue;
    const d = x.db.byNumber.get(t.num);
    const left = Math.max(1, g.healthOf(t.id));
    const v = d && isEmperor(d) ? (a.damage >= left ? 60 : a.damage * 2.5) : a.damage >= left ? worthOf(x, t) + 15 : (a.damage / left) * worthOf(x, t);
    best.set(a.from, Math.max(best.get(a.from) ?? 0, v));
  }
  return [...best.values()].reduce((n, v) => n + v, 0);
}

/** The bot's answer to a choice one of its cards is waiting for. Martin answers loosely; the others with care. */
function answerPick(x: Ctx, k: NonNullable<Game['state']['picks']>[number]): Action {
  const easy = x.level === 'easy';
  const any = <T,>(list: T[]) => list[Math.floor(x.rng() * list.length)];
  const base = { type: 'pick' as const, pick: k.id };
  if (k.yesNo) return { ...base, yes: !easy || x.rng() < 0.7 };
  if (k.numbers) {
    // what to pay (Dark Gerzite Titan) or add: Martin anything, Practice Buddy half of what he can spare, Creator all of it
    const gz = x.g.view(x.seat).players[x.seat].gerzite;
    return { ...base, numbers: k.numbers.map((n, i) => (i > 0 ? n.min : easy ? n.min + Math.floor(x.rng() * (n.max - n.min + 1)) : Math.max(n.min, Math.min(n.max, Math.floor((gz - 3) / (x.level === 'expert' ? 1 : 2)))))) };
  }
  if (k.arrange && k.cards) return { ...base, order: k.cards.map((c) => c.id), drop: [] };
  if (k.cards?.length) {
    // a card from a pile: the strongest (Martin: any)
    const best = easy ? any(k.cards) : [...k.cards].sort((a, b) => cardPower(x.db.byNumber.get(b.num)!) - cardPower(x.db.byNumber.get(a.num)!))[0];
    return { ...base, card: best.id };
  }
  if (k.choices?.length) return { ...base, choice: easy ? 1 + Math.floor(x.rng() * k.choices.length) : 1 };
  if (k.options.length) {
    const cards = k.options.map((id) => x.g.state.field.find((c) => c.id === id)).filter((c): c is FieldCard => !!c);
    const foes = cards.filter((c) => c.owner !== x.seat);
    const pool = foes.length ? foes : cards;
    if (!pool.length) return base;
    if (easy) return { ...base, card: any(pool).id, ...(k.split ? { amount: 1 } : {}) };
    // the opposing Emperor if the points would finish it, else the opposing card nearest to being destroyed that is worth most
    const left = k.split?.left ?? 1;
    const best = [...pool].sort((a, b) => {
      const va = x.g.healthOf(a.id) <= left ? 100 + worthOf(x, a) : worthOf(x, a) / Math.max(1, x.g.healthOf(a.id));
      const vb = x.g.healthOf(b.id) <= left ? 100 + worthOf(x, b) : worthOf(x, b) / Math.max(1, x.g.healthOf(b.id));
      return vb - va;
    })[0];
    return { ...base, card: best.id, ...(k.split ? { amount: Math.max(1, Math.min(left, x.g.healthOf(best.id))) } : {}) };
  }
  return base; // nothing to choose: pass
}

function attackPhase(x: Ctx, mem: BotMemory): BotStep | null {
  const s = x.g.state;
  const p = s.players[x.seat];
  if (p.turns === 0) return null; // nobody attacks on their first turn
  const ab = emperorAbility(x, mem);
  if (ab) return ab;
  const v = x.g.view(x.seat);
  // what each of its cards could do now: attack one of the targets the table rings for it, or use its Tap
  const moves: { card: FieldCard; action: Action; key: string; tap: boolean }[] = [];
  for (const c of s.field) {
    const cv = v.combat[c.id];
    const d = c.faceUp ? x.db.byNumber.get(c.num) : undefined;
    if (!cv || !d || cv.used && !(cv.left && cv.left > 0)) continue;
    if (!cv.why) for (const t of cv.targets) {
      const key = `strike:${c.id}:${t}:${cv.left ?? 'x'}`;
      if (!mem.tried.has(key)) moves.push({ card: c, action: { type: 'strike', from: c.id, to: t }, key, tap: false });
    }
    if (cv.tap && !cv.tapWhy && !cv.used && d.effects.some((e) => e.when === 'tap') && c.rot % 2 === 0 && !mem.tried.has(`tap:${c.id}`)) moves.push({ card: c, action: { type: 'tap', id: c.id, dir: 1, as: 'ability' }, key: `tap:${c.id}`, tap: true });
  }
  if (!moves.length) return null;
  const go = (m: (typeof moves)[number]): BotStep => {
    mem.tried.add(m.key);
    mem.attacked.add(m.card.id);
    return { make: m.action, delay: BOT_PACE.step[x.level], acts: true, ...{ key: m.key } } as BotStep;
  };
  const isEmp = (c: FieldCard) => isEmperor(x.db.byNumber.get(c.num)!);
  if (x.level === 'easy') {
    // Martin: his units one at a time (his Emperor last), each at any target it can reach; now and then he forgets one,
    // and a unit with a Tap uses it half the time
    const cards = [...new Set(moves.map((m) => m.card))].sort((a, b) => Number(isEmp(a)) - Number(isEmp(b)));
    for (const c of cards) {
      const mine = moves.filter((m) => m.card === c);
      if (x.rng() < 0.2) { for (const m of mine) mem.tried.add(m.key); continue; }
      const taps = mine.filter((m) => m.tap);
      const hits = mine.filter((m) => !m.tap);
      const m = taps.length && (!hits.length || x.rng() < 0.5) ? taps[0] : hits.length ? hits[Math.floor(x.rng() * hits.length)] : null;
      if (m) return go(m);
    }
    return null;
  }
  const scored = moves.map((m) => ({ m, v: trial(x, m.action) })).filter((o): o is { m: (typeof moves)[number]; v: number } => o.v !== null);
  if (!scored.length) { for (const m of moves) mem.tried.add(m.key); return null; }
  if (x.level === 'skilled') {
    // Practice Buddy: his hardest-hitting card first (his Emperor last), its best move with a little slack;
    // he skips only a move that clearly costs him
    const order = [...new Set(scored.map((o) => o.m.card))].sort((a, b) => Number(isEmp(a)) - Number(isEmp(b)) || hitOf(x, b, x.db.byNumber.get(b.num)!) - hitOf(x, a, x.db.byNumber.get(a.num)!));
    for (const c of order) {
      const mine = scored.filter((o) => o.m.card === c).map((o) => ({ ...o, v: o.v + x.rng() * 3 })).sort((a, b) => b.v - a.v);
      if (mine[0].v > -4) return go(mine[0].m);
      for (const o of mine) mem.tried.add(o.m.key);
    }
    return null;
  }
  // Creator: the single best move of all, every time, looking ahead to what his opponent could do back to him
  // next turn; he holds back when no move beats doing nothing (an untapped unit earns a Fractal instead)
  const now = threat(x, x.g);
  const ahead = moves.map((m) => { const r = tryOn(x, m.action); return r ? { m, v: r.v - THREAT * (threat(x, r.g) - now) } : null; }).filter((o): o is { m: (typeof moves)[number]; v: number } => !!o);
  const best = ahead.sort((a, b) => b.v - a.v)[0];
  if (!best || best.v <= 0.5) { for (const o of scored) mem.tried.add(o.m.key); return null; }
  return go(best.m);
}

// ---- Attack Phase (a table that leaves the damage to the players: CONFIG.combat.enabled off)
function attackPhaseManual(x: Ctx, mem: BotMemory): BotStep | null {
  const s = x.g.state;
  const p = s.players[x.seat];
  if (p.turns === 0) return null; // nobody attacks on their first turn
  const side = sideOf(x.seat);
  // the Emperor's abilities (skilled and expert, once a turn, with Gerzite to spare)
  const ab = emperorAbility(x, mem);
  if (ab) return ab;
  const blockers = ['entrenched', 'frozen', 'stunned', 'locked', 'comatose', CONFIG.turn.lifetime.expiredToken];
  // Forgotten Monarch (summoned at 7): tap it to Possess an enemy Unit within range for the rest of the turn
  const grab = monarchPossess(x, mem);
  if (grab) return grab;
  const ready = s.field.filter((c) => {
    const mine = c.owner === x.seat && !possessedByFoe(x, c); // a Unit its opponent has Possessed isn't the bot's to use
    const held = possessedByMe(x, c) && x.level !== 'easy' ? true : possessedByMe(x, c) && x.rng() < 0.7; // Martin forgets them now and then
    if (!(mine || held) || !c.faceUp || mem.attacked.has(c.id) || c.rot % 2 === 1) return false;
    const d = x.db.byNumber.get(c.num);
    if (!d || !(isUnitDef(d) || isEmperor(d)) || d.whileInPlay.includes('untouchable')) return false; // (a Calling Pillar has no attack)
    if (blockers.some((b) => has(x, c, b))) return false;
    // its own text can hold it back: "cannot attack until its Shield reaches 0" (Encased Skeleton), "cannot attack"
    if (/cannot attack until its shield reaches 0/i.test(d.text) ? valueOf(x, c, 'shield') > 0 : /\bcannot attack\b/i.test(d.text)) return false;
    const where = CONFIG.slots.find((sl) => inSlot(c, sl, c.owner === x.seat ? side : -side));
    return !!where && ['Lane', 'Minion', 'Emperor'].includes(slotKind(where.name));
  });
  // skilled and expert use the Units they have Possessed first: they go back to their owner soon
  if (x.level !== 'easy') ready.sort((a, b) => Number(possessedByMe(x, b)) - Number(possessedByMe(x, a)));
  // strongest first, so the big hits pick their targets first; the Emperor last (it can reach anything)
  ready.sort((a, b) => (x.level !== 'easy' ? Number(possessedByMe(x, b)) - Number(possessedByMe(x, a)) : 0) || Number(isEmperor(x.db.byNumber.get(a.num)!)) - Number(isEmperor(x.db.byNumber.get(b.num)!)) || hitOf(x, b, x.db.byNumber.get(b.num)!) - hitOf(x, a, x.db.byNumber.get(a.num)!));
  for (const c of ready) {
    const d = x.db.byNumber.get(c.num)!;
    mem.attacked.add(c.id);
    const dmg = hitOf(x, c, d);
    // a unit with a Tap ability and no attack of its own uses its Tap instead
    if (dmg <= 0 && !isEmperor(d)) {
      if (d.abilities.includes('tap') && (x.level !== 'easy' || x.rng() < 0.5)) {
        mem.queue.push({ make: { type: 'tap', id: c.id, dir: 1, as: 'ability' }, delay: BOT_PACE.step[x.level], acts: true });
        mem.queue.push(announce(x, `${d.name} uses its Tap — ${d.text}`, c.id));
        summonFromText(x, c, d, mem);
        return mem.queue.shift()!;
      }
      continue;
    }
    if (x.level === 'easy' && x.rng() < 0.2) continue; // forgets one now and then
    const held = possessedByMe(x, c);
    const target = held ? chooseTargetPossessed(x, c, d, dmg) : chooseTarget(x, c, d, dmg, mem);
    if (!target) continue;
    mem.queue.push({ make: { type: 'attack', from: c.id, to: target.cards[0].id }, delay: BOT_PACE.step[x.level], acts: true });
    // if the table rolled a miss for this attack (Fog, Smog, Ash Druid…), its damage stays off
    const missed = (g: Game) => !!g.state.lastMiss && g.state.lastMiss.from === c.id && g.state.lastMiss.missed;
    const unlessMissed = (st: BotStep): BotStep => ({ ...st, make: (g) => (missed(g) ? null : typeof st.make === 'function' ? st.make(g) : st.make) });
    const minion = minionOf(x, x.seat)?.id === c.id;
    // a Possessed Unit deals half its damage (all its hits together, rounded) to the enemy Emperor
    const atEmperor = held && target.cards[0].id === emperorOf(x, x.foe)?.id;
    const count = Math.max(1, valueOf(x, c, CONFIG.tokenRules.countToken) || 1);
    const hits = atEmperor ? 1 : count;
    const per = minion ? Math.ceil(dmg / 2) : atEmperor ? Math.round((dmg * count) / 2) : dmg;
    const pierce = traitNow(x, c, d, 'Pierce');
    const direct = traitNow(x, c, d, 'Direct');
    let thorns = 0;
    target.cards.forEach((t, k) => {
      const steps = damageSteps(x, t, per, { hits, pierce, direct, firstDelay: k === 0 ? BOT_PACE.arrow : 400 });
      mem.queue.push(...steps.map(unlessMissed));
      thorns += valueOf(x, t, 'thorns') * hits;
    });
    // a Unit whose Special Hit strikes back the first time it is damaged (Jungle Explorer): the bot's attacker
    // takes it, and the Special Hit is used up
    for (const t of target.cards) {
      const td = defOf(x, t);
      const back = td && has(x, t, 'special-hit') ? /first time this troop receives damage, deal (\d+)[^.]*?damage(?: and inflict (\w+))? on the unit that damaged/i.exec(td.text) : null;
      if (!back || isEmperor(d)) continue;
      const spent = tokensOn(x, t).find((o) => effectOf(x, o) === 'special-hit');
      mem.queue.push(...damageSteps(x, c, Number(back[1]), { spell: true, firstDelay: 600 }).map(unlessMissed));
      const status = back[2] ? x.tdb.list.find((k) => k.name.toLowerCase() === back[2].toLowerCase()) : undefined;
      if (status) mem.queue.push(...tokenSteps(x, status.id, c, status.counter ? status.start : undefined, 450).map(unlessMissed));
      if (spent) mem.queue.push(unlessMissed({ make: (g) => (g.state.tokens.some((o) => o.id === spent.id) ? { type: 'delete', id: spent.id } : null), delay: 400 }));
      mem.queue.push(unlessMissed(announce(x, `${td!.name}’s Special Hit strikes back: ${back[1]} damage${status ? ` and ${status.name}` : ''} on ${d.name}.`, t.id)));
    }
    if (thorns > 0 && !isEmperor(d)) mem.queue.push(...damageSteps(x, c, thorns, { spell: true, status: true, firstDelay: 500 }).map(unlessMissed));
    if (d.abilities.includes('tap') && d.text) mem.queue.push(announce(x, `${d.name} — ${d.text}`, c.id));
    mem.queue.push({ make: { type: 'tap', id: c.id, dir: 1, as: 'attack' }, delay: 500 });
    mem.queue.push({
      make: (g) => {
        const arrow = [...g.state.fx].reverse().find((f) => f.kind === 'attack' && f.seat === x.seat);
        return arrow ? { type: 'endAttack', arrow: arrow.id } : null;
      },
      delay: 700,
    });
    return mem.queue.shift()!;
  }
  return null;
}

/** Uses one of its Emperor's abilities, and says what it does (skilled and expert). */
function emperorAbility(x: Ctx, mem: BotMemory): BotStep | null {
  if (mem.usedAbility || x.level === 'easy') return null;
  const emp = emperorOf(x, x.seat);
  const def = emp ? x.db.byNumber.get(emp.num) : undefined;
  if (!emp || !def?.emperorAbilities.length) return null;
  if (CONFIG.emperorAbilities.blockedBy.some((b) => has(x, emp, b))) return null; // Entrenched, Frozen…: no abilities
  const gz = x.g.view(x.seat).players[x.seat].gerzite;
  const options = def.emperorAbilities.filter((a) => !a.dark && !a.costVaries && a.cost <= gz - (x.level === 'expert' ? 2 : 4) && !mem.tried.has(`ability:${a.slot}`));
  if (!options.length) return null;
  let ab = options.sort((a, b) => b.cost - a.cost)[0];
  if (CONFIG.combat.enabled) {
    // tried on a copy first: the one that does most (Creator only uses one that is worth its Gerzite)
    const tried = options.map((a) => ({ a, v: trial(x, { type: 'emperorAbility', id: emp.id, slot: a.slot }) })).filter((o): o is { a: (typeof options)[number]; v: number } => o.v !== null).map((o) => ({ ...o, v: o.v - o.a.cost * 0.25 + (x.level === 'skilled' ? x.rng() * 3 : 0) }));
    const best = tried.sort((p2, q) => q.v - p2.v)[0];
    if (!best || best.v <= (x.level === 'expert' ? 0.5 : -2)) { mem.usedAbility = true; return null; }
    ab = best.a;
    mem.usedAbility = true;
    mem.tried.add(`ability:${ab.slot}`);
    return { make: { type: 'emperorAbility', id: emp.id, slot: ab.slot }, delay: BOT_PACE.step[x.level], acts: true, ...{ key: `ability:${ab.slot}` } } as BotStep;
  }
  mem.usedAbility = true;
  mem.tried.add(`ability:${ab.slot}`);
  // (an ability the table carries out itself needs nothing more from the bot)
  if (!(CONFIG.combat.enabled && ab.effects.length)) abilityDamage(x, emp, def, ab.text, mem);
  mem.queue.push(announce(x, `${def.name} uses ${ab.name} — ${ab.text}`, emp.id));
  return { make: { type: 'emperorAbility', id: emp.id, slot: ab.slot }, delay: BOT_PACE.step[x.level], acts: true, ...{ key: `ability:${ab.slot}` } } as BotStep;
}

/**
 * An Emperor ability that simply deals damage now ("Deal 2 damage to an opposing Unit", "…to the opposing
 * Emperor", "…to all opposing Units"): the bot points at the target and puts the damage counters on it, as it
 * does for its attacks. Anything else the ability does is announced for the players to settle.
 */
function abilityDamage(x: Ctx, emp: FieldCard, def: CardDef, text: string, mem: BotMemory) {
  const m = /\bdeal\s+(\d+)\s+(true\s+|splash\s+|direct\s+)?damage\s+to\s+([^.;]*)/i.exec(text);
  if (!m) return;
  const n = Number(m[1]);
  const what = m[3].toLowerCase();
  if (n <= 0 || /\byou control\b|\byour\b(?! opponent)|both sides|affected by|^it\b/.test(what) || LATER.test(m[0])) return;
  const theirs = emperorOf(x, x.foe);
  let cards: FieldCard[] = [];
  if (/\bthis emperor\b|\bitself\b/.test(what)) cards = [emp]; // a cost it pays itself ("Deal 2 true damage to this Emperor")
  else if (/emperor/.test(what) && !/unit/.test(what)) cards = theirs ? [theirs] : [];
  else if (/\blane\b/.test(what)) {
    // a whole opposing lane: the one where it does the most
    const pick = bestSpellTarget(x, { ...def, damage: String(n), traits: ['Splash Damage'] } as CardDef);
    cards = pick ? pick.cards : [];
  } else if (/(\d+) different/.test(what)) {
    // several different Units: the best ones, one hit each
    const many = Number(/(\d+) different/.exec(what)![1]);
    const side = sideOf(x.seat);
    const all = lanes(-side).flatMap((l) => unitsIn(x, l.slot, -side)).filter((t) => !destroyed(x, t));
    const minion = minionOf(x, x.foe);
    if (minion && !destroyed(x, minion)) all.push(minion);
    cards = all.sort((a, b) => healthLeft(x, a) - healthLeft(x, b)).slice(0, many);
  } else if (/\ball\b/.test(what)) {
    const side = sideOf(x.seat);
    cards = lanes(-side).flatMap((l) => unitsIn(x, l.slot, -side)).filter((t) => !destroyed(x, t));
    const minion = minionOf(x, x.foe);
    if (minion && !destroyed(x, minion)) cards.push(minion);
  } else {
    const pick = bestSpellTarget(x, { ...def, damage: String(n), traits: [] } as CardDef);
    cards = pick ? pick.cards : theirs && /\bany\b|target/.test(what) ? [theirs] : [];
  }
  if (!cards.length) return;
  if (cards[0].id === emp.id) {
    mem.queue.push(...damageSteps(x, emp, n, { spell: true, direct: true, pierce: true, firstDelay: BOT_PACE.step[x.level] }));
    return;
  }
  mem.queue.push({ make: { type: 'attack', from: emp.id, to: cards[0].id, effect: true }, delay: BOT_PACE.arrow, acts: true });
  const direct = /direct|true/i.test(m[2] ?? '');
  cards.forEach((t, k) => mem.queue.push(...damageSteps(x, t, n, { spell: true, direct, pierce: direct, firstDelay: k === 0 ? BOT_PACE.arrow : 400 })));
  mem.queue.push({
    make: (g) => {
      const arrow = [...g.state.fx].reverse().find((f) => f.kind === 'attack' && f.seat === x.seat);
      return arrow ? { type: 'endAttack', arrow: arrow.id } : null;
    },
    delay: 600,
  });
}

/** Whom an attacker goes for: a lane's units (all of them for Splash), or the Emperor. */
function chooseTarget(x: Ctx, c: FieldCard, d: CardDef, dmg: number, mem?: BotMemory): { cards: FieldCard[]; score: number } | null {
  const side = sideOf(x.seat);
  const myLanes = lanes(side);
  const foeLanes = lanes(-side);
  const kind = slotKind(CONFIG.slots.find((sl) => inSlot(c, sl, side))!.name);
  const range = rangeNow(x, c, d);
  const leader = kind === 'Emperor' || kind === 'Minion';
  // a ground unit needs range to reach Flying units; an Emperor or Minion reaches anything
  const hitsFlying = leader || flies(x, c) || (range > 0 && !new RegExp(CONFIG.reach.noFlying, 'i').test(d.text));
  // Splash; a card that is "Splash Damage/Single Damage" splashes only with its Special Hit (Firelance's first attack)
  const splash = traitNow(x, c, d, 'Splash Damage');
  const theirEmp = emperorOf(x, x.foe);
  const theirMinion = minionOf(x, x.foe);
  const anyEmpty = foeLanes.some((l) => !unitsIn(x, l.slot, -side).length);
  const choices: { cards: FieldCard[]; score: number }[] = [];
  const unitScore = (t: FieldCard) => {
    const td = defOf(x, t);
    const left = healthLeft(x, t);
    let hit = dmg;
    if (has(x, t, 'entrenched')) hit = Math.ceil(hit / 2);
    if (valueOf(x, t, 'shield') > 0 && !traitNow(x, c, d, 'Pierce') && !traitNow(x, c, d, 'Direct')) hit = 0;
    hit = Math.max(0, hit - (traitNow(x, c, d, 'Pierce') ? 0 : valueOf(x, t, 'buffer')));
    const worth = (td?.cost ?? 3) + Math.max(0, ...numbersIn(td?.damage ?? '')) * 1.5;
    let sc = hit >= left ? 25 + worth : (hit / Math.max(1, left)) * worth * 0.7;
    const thorns = valueOf(x, t, 'thorns');
    if (thorns && x.level === 'expert' && thorns >= healthLeft(x, c)) sc -= 18;
    // a Special Hit that strikes back the first time it is damaged (Jungle Explorer): Creator weighs what it costs
    const back = x.level === 'expert' && td && has(x, t, 'special-hit') ? /first time this troop receives damage, deal (\d+)/i.exec(td.text) : null;
    if (back) sc -= Number(back[1]) >= healthLeft(x, c) ? 15 : 3;
    return sc;
  };
  const emperorScore = (t: FieldCard) => {
    const left = healthLeft(x, t);
    return dmg >= left ? 1000 : dmg * (x.level === 'expert' ? 1.6 : 1.2) + 4;
  };
  // a Unit the bot has Possessed is on its side for now: left alone, except by Creator once it has attacked
  // (it goes back to its owner soon, so it may as well be worn down after it has done its work)
  const spare = (t: FieldCard) => !possessedByMe(x, t) || (x.level === 'expert' && !!mem?.attacked.has(t.id));
  const addLane = (i: number) => {
    const units = unitsIn(x, foeLanes[i].slot, -side).filter((t) => !destroyed(x, t) && (hitsFlying || !flies(x, t)) && spare(t));
    if (!units.length) return;
    if (splash) choices.push({ cards: units, score: units.reduce((n, t) => n + unitScore(t), 0) });
    else for (const t of units) choices.push({ cards: [t], score: unitScore(t) });
  };
  if (leader) {
    foeLanes.forEach((_, i) => addLane(i));
    // (2v2: either opposing Emperor or Minion, the one across or the one on the diagonal, as the table allows)
    const legal = x.g.state.teams ? new Set(x.g.view(x.seat).combat?.[c.id]?.targets ?? []) : null;
    const foes = x.g.state.teams ? x.g.state.players.map((_, i) => i as Seat).filter((i) => i % 2 !== x.seat % 2 && !x.g.state.players[i].out) : [x.foe];
    for (const f of foes) {
      const emp = emperorOf(x, f);
      const min = minionOf(x, f);
      if (emp && (legal ? legal.has(emp.id) : anyEmpty)) choices.push({ cards: [emp], score: emperorScore(emp) });
      if (min && !destroyed(x, min) && (legal ? legal.has(min.id) : anyEmpty)) choices.push({ cards: [min], score: unitScore(min) });
    }
    void theirEmp; void theirMinion;
  } else {
    const i = myLanes.findIndex((l) => inSlot(c, l.slot, side));
    foeLanes.forEach((_, j) => Math.abs(j - i) <= range && addLane(j));
    const front = foeLanes[i];
    if (traitNow(x, c, d, 'Direct') && front && !unitsIn(x, front.slot, -side).length && theirEmp) choices.push({ cards: [theirEmp], score: emperorScore(theirEmp) });
  }
  if (!choices.length) return null;
  if (x.level === 'easy') return choices[Math.floor(x.rng() * choices.length)];
  if (x.level === 'skilled') for (const ch of choices) ch.score += x.rng() * 4;
  return choices.sort((a, b) => b.score - a.score)[0];
}

/**
 * A target for a Unit the bot has Possessed: it sits in its owner's lane and turns on its owner's side, so it
 * attacks their other Units within its range (and their Emperor if it can: Direct, at half damage). Creator
 * also likes sending it into Thorns, since the Thorns damage lands on its owner's card.
 */
function chooseTargetPossessed(x: Ctx, c: FieldCard, d: CardDef, dmg: number): { cards: FieldCard[]; score: number } | null {
  const side = -sideOf(x.seat); // the side of the table the Possessed Unit is on
  const own = lanes(side);
  const i = own.findIndex((l) => inSlot(c, l.slot, side));
  const range = rangeNow(x, c, d);
  const hitsFlying = flies(x, c) || (range > 0 && !new RegExp(CONFIG.reach.noFlying, 'i').test(d.text));
  const choices: { cards: FieldCard[]; score: number }[] = [];
  const score = (t: FieldCard) => {
    const td = defOf(x, t);
    const left = healthLeft(x, t);
    let hit = dmg;
    if (has(x, t, 'entrenched')) hit = Math.ceil(hit / 2);
    if (valueOf(x, t, 'shield') > 0 && !traitNow(x, c, d, 'Pierce') && !traitNow(x, c, d, 'Direct')) hit = 0;
    hit = Math.max(0, hit - (traitNow(x, c, d, 'Pierce') ? 0 : valueOf(x, t, 'buffer')));
    const worth = (td?.cost ?? 3) + Math.max(0, ...numbersIn(td?.damage ?? '')) * 1.5;
    let sc = hit >= left ? 25 + worth : (hit / Math.max(1, left)) * worth * 0.7;
    if (x.level === 'expert') sc += valueOf(x, t, 'thorns') * 2; // the Thorns hurt their own card
    return sc;
  };
  own.forEach((l, j) => {
    if (i < 0 || Math.abs(j - i) > range) return;
    for (const t of unitsIn(x, l.slot, side)) if (t.id !== c.id && !destroyed(x, t) && (hitsFlying || !flies(x, t)) && !possessedByMe(x, t)) choices.push({ cards: [t], score: score(t) });
  });
  const theirMinion = minionOf(x, x.foe);
  if (theirMinion && !destroyed(x, theirMinion) && range > 0) choices.push({ cards: [theirMinion], score: score(theirMinion) });
  const theirEmp = emperorOf(x, x.foe);
  if (theirEmp && traitNow(x, c, d, 'Direct')) {
    const half = Math.round(dmg / 2);
    choices.push({ cards: [theirEmp], score: half >= healthLeft(x, theirEmp) ? 1000 : half * 1.3 + 3 });
  }
  if (!choices.length) return null;
  if (x.level === 'easy') return choices[Math.floor(x.rng() * choices.length)];
  if (x.level === 'skilled') for (const ch of choices) ch.score += x.rng() * 4;
  return choices.sort((a, b) => b.score - a.score)[0];
}

/**
 * The opposing Unit most worth Possessing: one that hits hard and can attack (not a Minion or Emperor, not
 * already Possessed, not under Divinity). `near`: only Units within that card's range (Forgotten Monarch).
 * Easy picks any; skilled and expert the strongest, expert also passing over one that is tapped or can't attack.
 */
function bestToPossess(x: Ctx, near: { card: FieldCard; range: number } | null): FieldCard | null {
  const side = sideOf(x.seat);
  const foeLanes = lanes(-side);
  const from = near ? lanes(side).findIndex((l) => inSlot(near.card, l.slot, side)) : -1;
  const options = foeLanes.flatMap((l, j) => (near && (from < 0 || Math.abs(j - from) > near.range) ? [] : unitsIn(x, l.slot, -side))).filter((t) => {
    const td = defOf(x, t);
    return t.faceUp && isUnitDef(td) && !destroyed(x, t) && controllerOf(x, t) === null && !has(x, t, 'divinity');
  });
  if (!options.length) return null;
  if (x.level === 'easy') return options[Math.floor(x.rng() * options.length)];
  const blockers = ['frozen', 'stunned', 'locked', 'comatose'];
  const worth = (t: FieldCard) => {
    const td = defOf(x, t)!;
    let w = hitOf(x, t, td) * 2 + (td.cost ?? 3) + Math.min(10, healthLeft(x, t)) * 0.3;
    if (x.level === 'expert') {
      if (t.rot % 2 === 1 || blockers.some((b) => has(x, t, b))) w -= 12; // it couldn't attack for the bot anyway
      if (!hitOf(x, t, td)) w -= 8;
    }
    return w;
  };
  return options.sort((a, b) => worth(b) - worth(a))[0];
}

/** Forgotten Monarch, summoned at 7: taps to Possess an enemy Unit within range for the rest of the turn. */
function monarchPossess(x: Ctx, mem: BotMemory): BotStep | null {
  if (x.level === 'easy') return null;
  const s = x.g.state;
  for (const c of s.field) {
    if (c.owner !== x.seat || !c.faceUp || c.rot % 2 === 1 || mem.attacked.has(c.id) || possessedByFoe(x, c) || mem.tried.has(`grab:${c.id}`)) continue;
    const d = x.db.byNumber.get(c.num);
    if (!d || !/tap this troop to possess an enemy unit/i.test(d.text) || (d.forms?.length ?? 0) > 1 && (c.form ?? 0) !== d.forms!.length - 1) continue;
    if (['entrenched', 'frozen', 'stunned', 'locked', 'comatose'].some((b) => has(x, c, b))) continue;
    mem.tried.add(`grab:${c.id}`);
    const range = rangeNow(x, c, d);
    const unit = bestToPossess(x, { card: c, range });
    if (!unit) continue;
    // worth more than its own attack? (a Unit that hits at least as hard as it does)
    if (hitOf(x, unit, defOf(x, unit)!) < hitOf(x, c, d) * 0.6) continue;
    mem.attacked.add(c.id);
    mem.queue.push(...tokenSteps(x, 'possession', unit, 1, BOT_PACE.arrow));
    mem.queue.push({ make: { type: 'tap', id: c.id, dir: 1, as: 'ability' }, delay: 500 });
    mem.queue.push(announce(x, `${d.name} taps to Possess ${defOf(x, unit)?.name ?? 'a Unit'} for the rest of the turn.`, c.id));
    return { make: { type: 'attack', from: c.id, to: unit.id, effect: true }, delay: BOT_PACE.step[x.level], acts: true };
  }
  return null;
}

/** The best target for a damage Spell (a unit, or a lane for Splash), or null if there is nothing worth it. */
function bestSpellTarget(x: Ctx, def: CardDef): { cards: FieldCard[]; score: number } | null {
  const dmg = Math.max(0, ...numbersIn(def.damage), 0);
  if (!dmg) return null;
  const side = sideOf(x.seat);
  const foeLanes = lanes(-side);
  const choices: { cards: FieldCard[]; score: number }[] = [];
  const score = (t: FieldCard) => {
    const td = defOf(x, t);
    const left = healthLeft(x, t);
    const worth = (td?.cost ?? 3) + Math.max(0, ...numbersIn(td?.damage ?? '')) * 1.5;
    return dmg >= left ? 20 + worth : (dmg / Math.max(1, left)) * worth * 0.6;
  };
  for (const l of foeLanes) {
    const units = unitsIn(x, l.slot, -side).filter((t) => !destroyed(x, t));
    if (!units.length) continue;
    if (trait(def, 'Splash Damage')) choices.push({ cards: units, score: units.reduce((n, t) => n + score(t), 0) });
    else for (const t of units) choices.push({ cards: [t], score: score(t) });
  }
  const m = minionOf(x, x.foe);
  if (m && !destroyed(x, m)) choices.push({ cards: [m], score: score(m) + 3 });
  if (!choices.length) return null;
  if (x.level === 'easy') return choices[Math.floor(x.rng() * choices.length)];
  return choices.sort((a, b) => b.score - a.score)[0];
}

function mostHurt(x: Ctx): FieldCard | undefined {
  return x.g.state.field
    .filter((c) => c.owner === x.seat && c.faceUp && !destroyed(x, c) && tokensOn(x, c).some((t) => effectOf(x, t) === 'damage'))
    .sort((a, b) => healthLeft(x, a) - healthLeft(x, b))[0];
}

// ------------------------------------------------------------------ putting tokens on cards

/** A spot on a card for a new token, a little off its middle so it doesn't cover what's there. */
function spotOn(c: FieldCard, k = 0) {
  const sc = c.scale ?? 1;
  return { x: c.x + (((k % 3) - 1) * 1.6 + 0.8) * sc, z: c.z + 1.2 * sc };
}
/** Puts a token on a card: spawns it, then moves it onto the card. */
function tokenSteps(x: Ctx, tokenId: string, onto: FieldCard, value: number | undefined, delay: number): BotStep[] {
  return [
    { make: { type: 'spawnToken', tokenId, ...(value !== undefined ? { value } : {}), at: { x: onto.x, z: onto.z } }, delay, acts: true },
    {
      make: (g) => {
        const t = g.state.tokens.at(-1);
        const card = g.state.field.find((c) => c.id === onto.id);
        if (!t || t.tokenId !== tokenId || !card) return null;
        return { type: 'move', id: t.id, ...spotOn(card, tokensOnCard(card, g.state.tokens).length) };
      },
      delay: 350,
    },
  ];
}
/** Adds damage to a card: onto its Damage Counter if it has one, else a new one. */
function addDamage(x: Ctx, target: FieldCard, n: number, delay: number): BotStep[] {
  if (n <= 0) return [];
  const counter = tokensOn(x, target).find((t) => effectOf(x, t) === 'damage');
  if (counter) return [{ make: (g) => (g.state.tokens.some((t) => t.id === counter.id) ? { type: 'tokenValue', id: counter.id, delta: n } : null), delay, acts: true }];
  return tokenSteps(x, 'damage', target, n, delay);
}
/**
 * One attack's damage on a card, by the rules: each hit is halved while it is Entrenched, lowered by Buffer
 * unless it is Pierce, then soaked up whole by a Shield (which loses that much) unless it is Pierce.
 */
function damageSteps(x: Ctx, target: FieldCard, per: number, o: { hits?: number; pierce?: boolean; direct?: boolean; spell?: boolean; status?: boolean; firstDelay?: number } = {}): BotStep[] {
  const steps: BotStep[] = [];
  let shield = valueOf(x, target, 'shield');
  const shieldTok = tokensOn(x, target).find((t) => effectOf(x, t) === 'shield');
  const buffer = o.pierce ? 0 : valueOf(x, target, 'buffer');
  const halved = has(x, target, 'entrenched') && !o.status; // Entrenched halves damage from every source but status effects
  let total = 0;
  let shieldUsed = 0;
  for (let h = 0; h < (o.hits ?? 1); h++) {
    let hit = per;
    if (halved) hit = Math.ceil(hit / 2);
    hit = Math.max(0, hit - buffer);
    if (shield > 0 && !o.pierce && hit > 0) { // (only Pierce goes through a Shield: one layer that soaks up the whole hit, losing that much)
      const soak = Math.min(shield, hit);
      shield -= soak;
      shieldUsed += soak;
      continue;
    }
    total += hit;
  }
  const first = o.firstDelay ?? BOT_PACE.step[x.level];
  if (shieldUsed && shieldTok) steps.push({ make: (g) => (g.state.tokens.some((t) => t.id === shieldTok.id) ? { type: 'tokenValue', id: shieldTok.id, delta: -shieldUsed } : null), delay: first, acts: true });
  steps.push(...addDamage(x, target, total, steps.length ? 450 : first));
  return steps;
}
function healSteps(x: Ctx, target: FieldCard, n: number): BotStep[] {
  const counter = tokensOn(x, target).find((t) => effectOf(x, t) === 'damage');
  if (!counter) return [];
  return [{ make: (g) => (g.state.tokens.some((t) => t.id === counter.id) ? { type: 'tokenValue', id: counter.id, delta: -Math.min(n, counter.value) } : null), delay: BOT_PACE.step[x.level], acts: true }];
}

/** "…inflict Burn…": a status a card's text names goes on its target (only the ones that harm). */
function statusFromText(x: Ctx, def: CardDef, target: FieldCard, mem: BotMemory) {
  const text = def.text.toLowerCase();
  if (!/inflict|apply|applies|give|gains?/.test(text)) return;
  for (const id of CONFIG.hostileStatuses) {
    const tdef = x.tdb.byId.get(id) ?? x.tdb.list.find((t) => t.effect === id);
    if (!tdef) continue;
    const word = tdef.name.toLowerCase().replace(/ed$|en$/, '');
    if (!new RegExp(`\\b${word}`, 'i').test(text)) continue;
    const num = new RegExp(`(\\d+)\\s+(turns? of\\s+)?${word}`, 'i').exec(text)?.[1];
    mem.queue.push(...tokenSteps(x, tdef.id, target, tdef.counter ? Number(num ?? tdef.start) : undefined, BOT_PACE.step[x.level]));
    return;
  }
}

/** "…summon one Skell-Mini…": the summoned units a card's text names come onto the table. */
function summonFromText(x: Ctx, card: FieldCard, def: CardDef, mem: BotMemory) {
  const text = def.text;
  if (CONFIG.combat.enabled && def.effects.some((e) => e.words[0] === 'summon')) return; // the table summons these itself
  if (!/summon/i.test(text)) return;
  const unit = x.db.spawnOnly.find((u) => new RegExp(`\\b${u.name.replace(/[-]/g, '[- ]')}s?\\b`, 'i').test(text));
  if (!unit) return;
  const words: Record<string, number> = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4 };
  const m = new RegExp(`summon\\s+(\\w+)`, 'i').exec(text);
  const n = Math.min(4, m ? (words[m[1].toLowerCase()] ?? (Number(m[1]) || 1)) : 1);
  const side = sideOf(x.seat);
  const i = lanes(side).findIndex((l) => inSlot(card, l.slot, side));
  const intoFoe = /opponent'?s lane/i.test(text) && i >= 0;
  const where = intoFoe ? lanes(-side)[i] : i >= 0 ? lanes(side)[i] : { x: card.x, z: card.z };
  for (let k = 0; k < n; k++) {
    mem.queue.push({ make: { type: 'spawnCard', num: unit.number, at: { x: where.x, z: where.z } }, delay: BOT_PACE.step[x.level], acts: true });
    mem.queue.push({
      make: (g) => {
        const u = g.state.field.at(-1);
        return u && u.num === unit.number ? { type: 'move', id: u.id, x: where.x, z: where.z } : null;
      },
      delay: 350,
    });
  }
}

/** For the server's log: what kind of step this is (refused plays are keyed so they aren't tried again). */
export function stepKey(step: BotStep): string | undefined {
  return (step as BotStep & { key?: string }).key;
}
export { spanStarts };
